import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  fetchFeedUnreadCount,
  fetchTopics,
  fetchTopicCategories,
  fetchTopicDetail,
  fetchWordDetail,
  fetchWordTopics,
  getToken,
  setToken,
  logout,
  fetchCurrentUser,
  completeCheckout,
  completeInstituteCheckout,
  setAudioPlaybackAllowed,
  fetchOverallProgress,
  fetchNextReviewTopic,
  getUsageLimits,
  linkQuranVerb,
  unlinkQuranVerb,
  patchQuranWordMetadata,
} from "./api";
import TopicList from "./components/TopicList";
import TopicDetail from "./components/TopicDetail";
import GrammarLessonView from "./components/grammar/GrammarLessonView";
import WordDetail from "./components/WordDetail";
import GlobalSearch from "./components/GlobalSearch";
import TopicQuiz from "./components/TopicQuiz";
import AiTopicQuiz from "./components/quiz/AiTopicQuiz";
import CreateTopicModal from "./components/CreateTopicModal";
import RolePlayView from "./components/RolePlayView";
import LoginPage from "./components/LoginPage";
import SignupPage from "./components/SignupPage";
import ResetPasswordPage from "./components/ResetPasswordPage";
import SubscriptionRequired from "./components/SubscriptionRequired";
import InstitutePendingScreen from "./components/InstitutePendingScreen";
import InstituteAdminPanel from "./components/InstituteAdminPanel";
import ForcePasswordChange from "./components/ForcePasswordChange";
import VerifyPage from "./components/VerifyPage";
import AdminSettingsPanel from "./components/AdminSettingsPanel";
import WordBank from "./components/WordBank";
import WordBankAdminReview from "./components/WordBankAdminReview";
import AddWordModal from "./components/AddWordModal";
import Reading from "./components/Reading";
import TypingView from "./components/typing/TypingView";
import { Route, Routes, useNavigate } from "react-router-dom";
import { queueCheckoutEvents } from "./utils/analytics";
import MajlisPage from "./components/feed/MajlisPage";
import MajlisSubmitPage from "./components/feed/MajlisSubmitPage";
import FeedAdminReview from "./components/feed/FeedAdminReview";
import FloatingHelpButton from "./components/support/FloatingHelpButton";
import HelpPanel from "./components/support/HelpPanel";
import SupportAdminPanel from "./components/support/SupportAdminPanel";
import UserProfileMenu from "./components/UserProfileMenu";
import Leaderboard from "./components/Leaderboard";
import SpeakingHelper from "./components/SpeakingHelper";
import IrabTool from "./components/IrabTool";
import SarfTool from "./components/SarfTool";
import PracticeCallPage from "./components/PracticeCallPage";
import QuranicTopics from "./components/QuranicTopics";
import QuranicTopicDetail from "./components/QuranicTopicDetail";
import QuranVerbLinkPanel from "./components/QuranVerbLinkPanel";
import AiCreditBanner from "./components/AiCreditBanner";
import TrialEndingBanner from "./components/TrialEndingBanner";
import FirstRunWelcome from "./components/FirstRunWelcome";
import DailyGoalReminder from "./components/DailyGoalReminder";
import { getCorrectedCase, parseMorphology } from "./utils/arabicGrammar";
import ThemePreview from "./theme/ThemePreview";
import DiamondDivider from "./theme/DiamondDivider";

function App() {
  const params = new URLSearchParams(window.location.search);
  const verifyToken = params.get("token");
  // Deep-link params from practice-call email actions
  const practiceAction = params.get("action");      // "practice" → open practice panel
  const practiceResult = params.get("practice_result"); // "accepted"|"declined"|"expired"|"error"|"already_actioned"
  // Stripe Checkout return params (success_url / cancel_url set in the backend)
  const checkoutStatus = params.get("checkout");        // "success" | "cancel"
  const instituteCheckoutStatus = params.get("institute_checkout"); // "success" | "cancel"
  const checkoutSessionId = params.get("session_id");
  // Hand-off from the marketing landing page (/landing): ?signup=1&email=…
  const wantsSignup = params.get("signup") === "1";
  const signupEmail = params.get("email") || "";
  const [isAuthenticated, setIsAuthenticated] = useState(!!getToken());
  const [authPage, setAuthPage] = useState(
    checkoutStatus === "cancel" || instituteCheckoutStatus === "cancel" || wantsSignup ? "signup" : "login"
  );
  const [resetEmail, setResetEmail] = useState("");
  const [isAdmin, setIsAdmin] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);
  // True while we finalize a just-completed Stripe checkout (signup OR resubscribe).
  // Runs whether or not the user is already logged in.
  const [checkoutProcessing, setCheckoutProcessing] = useState(
    (checkoutStatus === "success" || instituteCheckoutStatus === "success") && !!checkoutSessionId
  );
  const [checkoutError, setCheckoutError] = useState(null);
  // Which completion endpoint the return should hit (institute checkouts sync
  // the Institute row instead of a personal Subscription).
  const isInstituteCheckoutReturn = instituteCheckoutStatus === "success";

  const [topics, setTopics] = useState([]);
  // Sidebar categories (admin-defined). selectedCategoryId null = top level.
  const [topicCategories, setTopicCategories] = useState([]);
  const [selectedCategoryId, setSelectedCategoryId] = useState(null);
  const [topicsLoading, setTopicsLoading] = useState(true);
  const [topicsError, setTopicsError] = useState(null);

  const [selectedTopicId, setSelectedTopicId] = useState(null);
  const [selectedTopic, setSelectedTopic] = useState(null);
  const [topicLoading, setTopicLoading] = useState(false);
  const [topicError, setTopicError] = useState(null);
  // Grammar-lesson topics (is_grammar_lesson) render GrammarLessonView instead
  // of TopicDetail and never fetch the regular topic-detail payload.
  const selectedTopicIsGrammarLesson = !!topics.find((t) => t.id === selectedTopicId)?.is_grammar_lesson;

  const [selectedQuranicTopicId, setSelectedQuranicTopicId] = useState(null);
  const [selectedQuranicTopicIsBuiltin, setSelectedQuranicTopicIsBuiltin] = useState(false);
  const [selectedQuranicWord, setSelectedQuranicWord] = useState(null);
  const [showAdvancedMorphology, setShowAdvancedMorphology] = useState(false);
  const [editingMorphology, setEditingMorphology] = useState(false);
  const [morphologyEditValues, setMorphologyEditValues] = useState({});
  const [morphologySaveResult, setMorphologySaveResult] = useState(null); // { changed: [...] }
  const [morphologySaving, setMorphologySaving] = useState(false);
  const [verbLinkLoading, setVerbLinkLoading] = useState(false);

  // Derived grammar data for the currently selected Quranic word
  const parsedMorphology = useMemo(
    () => parseMorphology(selectedQuranicWord?.word?.metadata?.morphology_summary),
    [selectedQuranicWord]
  );
  const correctedCase = useMemo(
    () => getCorrectedCase(selectedQuranicWord?.word?.arabic, selectedQuranicWord?.word?.metadata),
    [selectedQuranicWord]
  );
  const [showQuranicQuiz, setShowQuranicQuiz] = useState(false);
  const [quranicProgress, setQuranicProgress] = useState(null); // { completed, total }
  const [builtinRefreshKey, setBuiltinRefreshKey] = useState(0);

  const [selectedWordId, setSelectedWordId] = useState(null);
  const [wordSheetOpen, setWordSheetOpen] = useState(false);
  const [selectedWord, setSelectedWord] = useState(null);
  const [selectedWordType, setSelectedWordType] = useState(null);
  // Occurrence context when the word was opened from a scenario sentence:
  // { surfaceText, morphology, turnId, tokenIdx } — the surface form can
  // differ from Word.text in its case ending (#92).
  const [selectedWordOccurrence, setSelectedWordOccurrence] = useState(null);
  const [wordLoading, setWordLoading] = useState(false);
  const [wordError, setWordError] = useState(null);

  const [wordTopics, setWordTopics] = useState([]);
  const [wordTopicsLoading, setWordTopicsLoading] = useState(false);
  const [wordTopicsError, setWordTopicsError] = useState(null);

  // Scenario line to scroll to / flash after opening a topic from search:
  // { turnId, nonce } — nonce forces a re-scroll when the same line is picked twice.
  const [focusTurn, setFocusTurn] = useState(null);
  const [addWordModalText, setAddWordModalText] = useState(null); // non-null = modal open

  const [breadcrumb, setBreadcrumb] = useState({ fromTopic: null, wordText: null, toTopic: null });

  const [showQuiz, setShowQuiz] = useState(false);
  const [showAiQuiz, setShowAiQuiz] = useState(false); // AI topic quiz (docs/AI_TOPIC_QUIZ.md)
  const [showChat, setShowChat] = useState(false);
  const [showFreeChat, setShowFreeChat] = useState(false);
  const [showCreateTopic, setShowCreateTopic] = useState(false);
  const [quizMode, setQuizMode] = useState("learn");
  const [overallProgress, setOverallProgress] = useState(null);
  const [showAdminSettings, setShowAdminSettings] = useState(false);
  const [showInstitutePanel, setShowInstitutePanel] = useState(false);
  const [showWordBank, setShowWordBank] = useState(false);
  const [showWordBankReview, setShowWordBankReview] = useState(false);
  const [showReading, setShowReading] = useState(false);
  const [showTyping, setShowTyping] = useState(false);
  const [showFeedAdmin, setShowFeedAdmin] = useState(false);
  const [showHelp, setShowHelp] = useState(false);
  const [showSupportAdmin, setShowSupportAdmin] = useState(false);
  const [feedEnabled, setFeedEnabled] = useState(false);
  const [showIrabTool, setShowIrabTool] = useState(false);
  // Text handed to the Iʿrāb tool by a per-ayah button (opens prefilled + analyzed)
  const [irabToolText, setIrabToolText] = useState(null);
  // Quranic ayāt: only admins may trigger an analysis; everyone else opens the
  // tool read-only and sees the analysis an admin already ran (or a "not yet
  // analyzed" note). Set alongside irabToolText, cleared with it.
  const [irabToolViewOnly, setIrabToolViewOnly] = useState(false);
  const openQuranicIrab = (text) => { setIrabToolViewOnly(!isAdmin); setIrabToolText(text); };
  const [showSarfTool, setShowSarfTool] = useState(false);
  const [feedButtonHidden, setFeedButtonHidden] = useState(false);
  const [feedUnread, setFeedUnread] = useState(0);
  const [showLeaderboard, setShowLeaderboard] = useState(false);
  const [showSpeakingHelper, setShowSpeakingHelper] = useState(false);
  const [showPractice, setShowPractice] = useState(false);
  const [practiceMinimized, setPracticeMinimized] = useState(false);
  const [pipPos, setPipPos] = useState(null); // { left, top } when minimized
  const pipRef = useRef(null);
  const [practiceResultBanner, setPracticeResultBanner] = useState(null);
  const [practiceCallsButtonHidden, setPracticeCallsButtonHidden] = useState(false);

  const wordDetailPanelRef = useRef(null);
  const skipQuizResetRef = useRef(false);
  const [activeTab, setActiveTab] = useState("topics");
  const [showQuranSection, setShowQuranSection] = useState(false);

  // Finalize a just-completed Stripe checkout: verify the session, activate the
  // account, log in, then clean the URL.
  useEffect(() => {
    if (!checkoutProcessing) return;
    let cancelled = false;
    const complete = isInstituteCheckoutReturn ? completeInstituteCheckout : completeCheckout;
    complete(checkoutSessionId)
      .then((data) => {
        if (cancelled) return;
        setToken(data.token);
        // Stage analytics events in sessionStorage — the reload below would
        // kill them if sent now, before the tracker script has loaded.
        queueCheckoutEvents();
        // Full reload to "/" so state (has_access, subscription) is re-fetched
        // fresh — covers resubscribe, where the user is already logged in and a
        // setIsAuthenticated(true) would not retrigger the user fetch.
        window.location.replace("/");
      })
      .catch((err) => {
        if (cancelled) return;
        setCheckoutError(err.message || "Could not verify your payment.");
        setCheckoutProcessing(false);
      });
    return () => { cancelled = true; };
  }, [checkoutProcessing, checkoutSessionId, isInstituteCheckoutReturn]);

  // Fetch current user
  useEffect(() => {
    if (!isAuthenticated) { setIsAdmin(false); setCurrentUser(null); return; }
    fetchCurrentUser()
      .then((me) => {
        setIsAdmin(!!me.is_staff);
        setCurrentUser(me);
        // No app access (e.g. a management-only institute admin without a
        // seat) → hide all TTS play buttons and block synthesis calls.
        setAudioPlaybackAllowed(me.has_access !== false);
      })
      .catch(() => { setIsAdmin(false); });
    getUsageLimits()
      .then((limits) => {
        setPracticeCallsButtonHidden(limits?.practice_calls_button_hidden ?? false);
        setFeedEnabled(limits?.feed_enabled ?? false);
        setFeedButtonHidden(limits?.feed_button_hidden ?? false);
      })
      .catch(() => {});
  }, [isAuthenticated]);

  // The feed lives at /majlis — the app's first routed page. The page itself
  // stamps feed_seen_at on mount (covers deep links and reloads, not just
  // this button) and clears the badge via onSeen.
  const navigate = useNavigate();
  const openFeed = () => navigate("/majlis");

  // Feed unread badge — polled every 5 minutes like AiCreditBanner.
  useEffect(() => {
    if (!isAuthenticated) return;
    let cancelled = false;
    const load = () =>
      fetchFeedUnreadCount()
        .then((d) => { if (!cancelled) setFeedUnread(d.unread || 0); })
        .catch(() => {});
    load();
    const timer = setInterval(load, 5 * 60 * 1000);
    return () => { cancelled = true; clearInterval(timer); };
  }, [isAuthenticated]);

  // Handle deep-links from practice-call emails (?action=practice&practice_result=...)
  useEffect(() => {
    if (!isAuthenticated) return;
    // URL params (direct link while already logged in, or after login with URL still intact)
    if (practiceAction === "practice") {
      setShowPractice(true);
      window.history.replaceState({}, "", "/");
    }
    // sessionStorage intent (user was not logged in, logged in via login form)
    if (sessionStorage.getItem("practiceDeepLink")) {
      setShowPractice(true);
      sessionStorage.removeItem("practiceDeepLink");
    }
    const storedResult = sessionStorage.getItem("practiceDeepLinkResult") || practiceResult;
    if (storedResult) {
      const msgs = {
        accepted: "✅ You accepted the practice call. Both partners will be notified once confirmed.",
        declined: "You declined the practice call. You've been returned to the lobby.",
        expired: "⏱ This link has expired. Please open the app to respond.",
        already_actioned: "This session has already been actioned.",
        error: "Something went wrong with that link. Please open the app.",
      };
      setPracticeResultBanner(msgs[storedResult] || "Something went wrong with that link.");
      sessionStorage.removeItem("practiceDeepLinkResult");
      window.history.replaceState({}, "", "/");
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated]);

  const loadProgress = async () => {
    try {
      const data = await fetchOverallProgress();
      setOverallProgress(data);
    } catch (err) {
      console.error("Could not load progress", err);
    }
  };

  const refreshTopicDetail = async () => {
    if (!selectedTopicId) return;
    try {
      const data = await fetchTopicDetail(selectedTopicId);
      setSelectedTopic(data);
    } catch (err) {
      console.error("Could not refresh topic detail", err);
    }
  };

  // Role-play finished: merge the confirmed progression straight into the
  // in-memory topic so the guided journey shows the step complete the instant
  // the user exits — no refetch race against the backend commit. Falls back to
  // a refetch if the payload didn't include progression.
  const handleRolePlayCompleted = (progression) => {
    if (progression) {
      setSelectedTopic((t) => (t ? { ...t, progression } : t));
    } else {
      refreshTopicDetail();
    }
  };

  useEffect(() => {
    if (!isAuthenticated) { setOverallProgress(null); return; }
    loadProgress();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated]);

  useEffect(() => {
    if (!isAuthenticated) { setTopics([]); setTopicsLoading(false); return; }
    async function loadTopics() {
      try {
        setTopicsLoading(true);
        const data = await fetchTopics();
        const sorted = [...data].sort((a, b) => {
          const ao = a.order ?? 999999;
          const bo = b.order ?? 999999;
          if (ao !== bo) return ao - bo;
          return (a.name || "").localeCompare(b.name || "");
        });
        setTopics(sorted);
        // Auto-select the first topic on initial load. This is not a user-driven
        // topic switch, so it must not clobber an activity (Free Chat / Role Play /
        // Quiz) the user opened from the top bar while topics were still loading —
        // otherwise the selectedTopicId effect below tears the panel down and drops
        // the user onto the first topic's detail. Guard that reset for this one change.
        if (sorted.length > 0) {
          skipQuizResetRef.current = true;
          setSelectedTopicId(sorted[0].id);
        }
      } catch (err) {
        console.error(err);
        setTopicsError("Could not load topics.");
      } finally {
        setTopicsLoading(false);
      }
    }
    loadTopics();
    fetchTopicCategories().then(setTopicCategories).catch((err) => console.error("Could not load topic categories", err));
  }, [isAuthenticated]);

  useEffect(() => {
    if (!selectedTopicId) { setSelectedTopic(null); return; }
    if (!skipQuizResetRef.current) {
      setShowChat(false); setShowFreeChat(false); setShowQuiz(false);
    }
    skipQuizResetRef.current = false;
    if (selectedTopicIsGrammarLesson) { setSelectedTopic(null); return; }
    async function loadTopicDetailData() {
      try {
        setTopicLoading(true); setTopicError(null);
        const data = await fetchTopicDetail(selectedTopicId);
        setSelectedTopic(data);
      } catch (err) {
        console.error(err); setTopicError("Could not load topic details.");
      } finally {
        setTopicLoading(false);
      }
    }
    loadTopicDetailData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedTopicId]);

  useEffect(() => {
    if (!selectedWordId) { setSelectedWord(null); return; }
    async function loadWordDetail() {
      try {
        setWordLoading(true); setWordError(null);
        const data = await fetchWordDetail(selectedWordId);
        setSelectedWord(data);
      } catch (err) {
        console.error(err); setWordError("Could not load word details.");
      } finally {
        setWordLoading(false);
      }
    }
    loadWordDetail();
  }, [selectedWordId]);

  useEffect(() => {
    if (!selectedWordId) { setWordTopics([]); return; }
    async function loadWordTopics() {
      try {
        setWordTopicsLoading(true); setWordTopicsError(null);
        const data = await fetchWordTopics(selectedWordId);
        setWordTopics(data);
      } catch (err) {
        console.error(err); setWordTopicsError("Could not load topics for this word.");
      } finally {
        setWordTopicsLoading(false);
      }
    }
    loadWordTopics();
  }, [selectedWordId]);

  useEffect(() => {
    if (!selectedWordId) return;
    if (!wordTopics || wordTopics.length === 0) return;
    const alreadyInList = selectedTopicId && wordTopics.some((t) => t.id === selectedTopicId);
    if (!alreadyInList) {
      const firstTopic = wordTopics[0];
      setSelectedTopicId(firstTopic.id);
      setBreadcrumb((prev) => ({ fromTopic: prev.fromTopic || "Search", wordText: prev.wordText, toTopic: firstTopic.name }));
    }
  }, [wordTopics, selectedWordId, selectedTopicId]);

  useEffect(() => {
    if (wordDetailPanelRef.current) wordDetailPanelRef.current.scrollTop = 0;
  }, [selectedWordId]);

  useEffect(() => {
    setShowAdvancedMorphology(false);
    setEditingMorphology(false);
    setMorphologySaveResult(null);
  }, [selectedQuranicWord]);

  const handleWordSelect = (wordId, wordText, wordType = null, occurrence = null) => {
    setSelectedWordId(wordId);
    setSelectedWordType(wordType);
    setSelectedWordOccurrence(occurrence);
    const crumbText = occurrence?.surfaceText || wordText;
    setBreadcrumb((prev) => ({ fromTopic: selectedTopic ? selectedTopic.name : prev.fromTopic, wordText: crumbText, toTopic: null }));
  };

  const handleClearWord = () => {
    setSelectedWordId(null); setSelectedWord(null); setSelectedWordType(null); setSelectedWordOccurrence(null); setWordTopics([]); setWordTopicsError(null);
    setWordSheetOpen(false);
    setBreadcrumb((prev) => ({ fromTopic: selectedTopic ? selectedTopic.name : prev.fromTopic, wordText: null, toTopic: null }));
  };

  // ── Global search selections ──────────────────────────────────────────────
  const handleSearchWord = (w) => {
    setSelectedWordId(w.id); setSelectedTopicId(null); setSelectedTopic(null); setSelectedWordOccurrence(null);
    setBreadcrumb({ fromTopic: "Search", wordText: w.text, toTopic: null });
    setShowQuiz(false); setShowChat(false); setShowFreeChat(false);
    setActiveTab("search");
  };
  const openTopicFromSearch = (topicId, topicName, turnId = null) => {
    setSelectedQuranicTopicId(null); setSelectedQuranicWord(null); setShowQuranicQuiz(false);
    setShowQuiz(false); setShowChat(false); setShowFreeChat(false);
    setSelectedWordId(null); setSelectedWord(null); setWordTopics([]); setWordTopicsError(null);
    setFocusTurn(turnId ? { turnId, nonce: Date.now() } : null);
    setSelectedTopicId(topicId);
    revealTopicCategory(topicId);
    setBreadcrumb({ fromTopic: topicName, wordText: null, toTopic: null });
    setActiveTab("detail");
  };
  const handleSearchSentence = (s) => {
    if (s.kind === "turn" && s.topic) openTopicFromSearch(s.topic.id, s.topic.name, s.id);
    else if (s.kind === "example" && s.word) handleSearchWord({ id: s.word.id, text: s.word.text });
  };
  const handleSearchTopic = (t) => openTopicFromSearch(t.id, t.name);

  const handleWordCreated = (word) => {
    setAddWordModalText(null);
    if (!word) return;
    setSelectedWordId(word.id); setSelectedTopicId(null); setSelectedTopic(null); setSelectedWordOccurrence(null);
    setBreadcrumb({ fromTopic: "Search", wordText: word.text, toTopic: null });
    setShowQuiz(false); setShowChat(false); setShowFreeChat(false);
    setActiveTab("search");
  };

  const handleTopicSelectFromWord = (topicId, topicName) => {
    setSelectedTopicId(topicId); revealTopicCategory(topicId); setShowChat(false); setShowFreeChat(false); setShowQuiz(false);
    setBreadcrumb((prev) => ({ fromTopic: prev.fromTopic || (selectedTopic ? selectedTopic.name : null), wordText: prev.wordText, toTopic: topicName }));
  };

  const handleFormWordClick = (wordId, wordText) => {
    if (!wordId) return;
    setSelectedWordId(wordId); setSelectedTopicId(null); setSelectedTopic(null); setSelectedWordOccurrence(null);
    setBreadcrumb({ fromTopic: selectedTopic ? selectedTopic.name : "Form", wordText, toTopic: null });
    setWordTopics([]); setWordTopicsError(null);
  };

  const handleTopicWordTypeChange = async (wordId, newType, result) => {
    // Refresh the current topic after a word type change so the word lists update.
    if (selectedTopicId) {
      try {
        const data = await fetchTopicDetail(selectedTopicId);
        setSelectedTopic(data);
      } catch (err) { console.error(err); }
    }
    // If the word was removed (conjugated form merged into root verb), clear selection.
    if (result && result.removed) {
      setSelectedWordId(null); setSelectedWord(null); setSelectedWordType(null);
    } else {
      setSelectedWordType(newType);
    }
  };

  const handleLogout = () => { logout(); setIsAuthenticated(false); setIsAdmin(false); setCurrentUser(null); setAuthPage("login"); setResetEmail(""); };

  const [reviewLoading, setReviewLoading] = useState(false);
  const [reviewError, setReviewError] = useState(null);

  const handleStartReview = async () => {
    try {
      setReviewLoading(true); setReviewError(null);
      const data = await fetchNextReviewTopic();
      if (!data.topic) { setReviewError("Score \u2265 80% on a topic quiz to unlock Review."); return; }
      skipQuizResetRef.current = true;
      setSelectedTopicId(data.topic.id);
      // Open the sidebar on the category holding the reviewed topic so the
      // highlighted card is visible (null = top level for uncategorised).
      setSelectedCategoryId(data.topic.category ?? null);
      setBreadcrumb({ fromTopic: data.topic.name, wordText: null, toTopic: null });
      setQuizMode("review"); setShowChat(false); setShowFreeChat(false); setShowQuiz(true);
      setSelectedWordId(null); setSelectedWord(null); setWordTopics([]);
      setActiveTab("detail");
    } catch (err) {
      console.error(err); setReviewError("Could not start review.");
    } finally {
      setReviewLoading(false);
    }
  };

  const reloadTopics = async () => {
    try {
      const data = await fetchTopics();
      const sorted = [...data].sort((a, b) => {
        const ao = a.order ?? 999999; const bo = b.order ?? 999999;
        if (ao !== bo) return ao - bo;
        return (a.name || "").localeCompare(b.name || "");
      });
      setTopics(sorted);
      setTopicCategories(await fetchTopicCategories());
    } catch (err) { console.error(err); }
  };

  // Admin edited categories (create/rename/reorder/delete) in the sidebar
  // manager. Topics whose category was removed drop back to the top level,
  // and a deleted category can't stay open.
  const handleCategoriesChanged = (next) => {
    const ids = new Set(next.map((c) => c.id));
    setTopicCategories(next);
    setTopics((prev) => prev.map((t) => (t.category != null && !ids.has(t.category) ? { ...t, category: null } : t)));
    setSelectedCategoryId((cur) => (cur != null && !ids.has(cur) ? null : cur));
  };

  // Admin moved a topic between categories (from the topic header).
  const handleTopicCategoryChanged = (topicId, categoryId) => {
    setTopics((prev) => prev.map((t) => (t.id === topicId ? { ...t, category: categoryId } : t)));
    setSelectedTopic((prev) => (prev && prev.id === topicId ? { ...prev, category: categoryId } : prev));
  };

  // Jumping to a topic from outside the list (search, a word's topics) opens
  // its category in the sidebar so the highlighted card is actually visible.
  const revealTopicCategory = (topicId) => {
    const t = topics.find((x) => x.id === topicId);
    setSelectedCategoryId(t && t.category != null ? t.category : null);
  };

  // Step 1 (dark theme) foundation preview — open "?themepreview=1" to verify
  // the tokens, fonts, shared surface and diamond divider. Temporary.
  if (params.get("themepreview") === "1") {
    return <ThemePreview />;
  }

  // Finalizing a Stripe checkout return
  if (checkoutProcessing) {
    return (
      <div className="dark-scope min-h-screen flex items-center justify-center px-4" style={{ background: "var(--ink)" }}>
        <div className="text-center">
          <div className="w-10 h-10 mx-auto mb-4 border-2 border-ink-300 border-t-ink-600 rounded-full animate-spin" />
          <p className="text-sm text-slate-500">Confirming your subscription…</p>
        </div>
      </div>
    );
  }
  if (checkoutError) {
    return (
      <div className="dark-scope min-h-screen flex items-center justify-center px-4" style={{ background: "var(--ink)" }}>
        <div className="surface rounded-2xl shadow-soft-lg p-8 max-w-sm w-full text-center">
          <h2 className="text-xl font-display font-semibold text-ink-800 mb-2">Something went wrong</h2>
          <p className="text-sm text-rose-700 bg-rose-50 border border-rose-200 rounded-lg px-3 py-2 mb-4">{checkoutError}</p>
          <button
            onClick={() => { setCheckoutError(null); window.history.replaceState({}, "", "/"); setAuthPage("login"); }}
            className="w-full py-2.5 rounded-xl bg-ink-600 text-parchment-50 font-semibold text-sm hover:bg-ink-700 transition-colors"
          >
            Back to sign in
          </button>
        </div>
      </div>
    );
  }

  // Auth pages
  if (verifyToken) {
    return <VerifyPage verifyToken={verifyToken} onVerified={() => { window.history.replaceState({}, "", "/"); setIsAuthenticated(true); }} />;
  }
  if (!isAuthenticated) {
    // Persist deep-link intent so it survives the login redirect
    if (practiceAction === "practice" && typeof window !== "undefined") {
      sessionStorage.setItem("practiceDeepLink", "1");
      if (practiceResult) sessionStorage.setItem("practiceDeepLinkResult", practiceResult);
    }
    if (authPage === "reset") {
      return (
        <ResetPasswordPage
          initialEmail={resetEmail}
          onGoToLogin={() => setAuthPage("login")}
          onReset={() => { window.history.replaceState({}, "", "/"); setIsAuthenticated(true); }}
        />
      );
    }
    if (authPage === "signup") {
      return (
        <SignupPage
          initialEmail={signupEmail}
          onGoToLogin={() => setAuthPage("login")}
          onGoToReset={(email) => { setResetEmail(email || ""); setAuthPage("reset"); }}
          onVerified={() => { window.history.replaceState({}, "", "/"); setIsAuthenticated(true); }}
        />
      );
    }
    return (
      <LoginPage
        onLoggedIn={() => setIsAuthenticated(true)}
        onGoToSignup={() => setAuthPage("signup")}
        onForgotPassword={() => { setResetEmail(""); setAuthPage("reset"); }}
      />
    );
  }

  // Institute-created account still on its temporary password → must change it
  // first (the backend also blocks all learning endpoints until then).
  if (currentUser && currentUser.force_password_change) {
    return (
      <ForcePasswordChange
        onChanged={() =>
          fetchCurrentUser().then((me) => { setIsAdmin(!!me.is_staff); setCurrentUser(me); })
        }
        onLogout={handleLogout}
      />
    );
  }

  // Institute paid but awaiting site-admin approval → holding screen for all members.
  if (currentUser && currentUser.institute?.status === "pending") {
    return <InstitutePendingScreen user={currentUser} onLogout={handleLogout} />;
  }

  // An institute admin without a learner seat has has_access=false but must
  // still reach the app shell to manage members/billing (learning APIs stay
  // blocked server-side by HasAppAccess).
  const isInstituteManager =
    currentUser?.institute?.role === "admin" && currentUser.institute.status === "active";

  // Subscription paywall: authenticated but lapsed → block the app, offer resubscribe.
  if (currentUser && currentUser.has_access === false && !isInstituteManager) {
    return <SubscriptionRequired user={currentUser} onLogout={handleLogout} />;
  }

  const topicProgressMap = {};
  if (overallProgress?.topics) {
    for (const t of overallProgress.topics) {
      topicProgressMap[t.id] = { best_score: t.best_score, progress_percent: t.progress_percent, completed: t.completed };
    }
  }

  // Sidebar content
  const SidebarContent = () => (
    <>
      {/* Section switcher */}
      <div className="surface-glass flex rounded-xl overflow-hidden mb-3">
        <button
          onClick={() => {
            // Entering the Speaking section from Quran: open on the first topic so
            // the detail pane isn't empty. Guarded by showQuranSection so re-clicking
            // Speaking while already here doesn't clobber the current topic/word view.
            if (showQuranSection && topics.length > 0) {
              setSelectedTopicId(topics[0].id);
              setSelectedWordId(null); setSelectedWord(null);
            }
            setShowQuranSection(false);
            setSelectedQuranicTopicId(null); setSelectedQuranicWord(null); setShowQuranicQuiz(false);
          }}
          className="flex-1 py-1.5 text-xs font-medium transition-colors"
          style={!showQuranSection
            ? { background: "var(--gold-bg)", color: "var(--gold-bright)" }
            : { background: "transparent", color: "var(--parchment-dim)" }}
        >
          Speaking
        </button>
        <button
          onClick={() => {
            setShowQuranSection(true);
            setSelectedTopicId(null); setSelectedTopic(null);
            setSelectedWordId(null); setSelectedWord(null);
            setShowQuiz(false); setShowChat(false); setShowFreeChat(false);
          }}
          className="flex-1 py-1.5 text-xs font-medium transition-colors border-x"
          style={showQuranSection
            ? { background: "var(--gold-bg)", color: "var(--gold-bright)", borderColor: "var(--panel-line)" }
            : { background: "transparent", color: "var(--parchment-dim)", borderColor: "var(--panel-line)" }}
        >
          🕌 Quran
        </button>
        <button
          onClick={() => setShowReading(true)}
          className="flex-1 py-1.5 text-xs font-medium transition-colors"
          style={{ background: "transparent", color: "var(--parchment-dim)" }}
        >
          📖 Reading
        </button>
      </div>

      {/* Progress block — different for Topics vs Quran */}
      {!showQuranSection && overallProgress && (
        <div className="mb-4 px-1">
          <div className="flex justify-between text-xs mb-1" style={{ color: "var(--parchment-dim)" }}>
            <span>Topics completed</span>
            <span className="tabular-nums font-semibold" style={{ color: "var(--parchment)" }}>
              {overallProgress.completed_topics}/{overallProgress.total_topics}
            </span>
          </div>
          <div className="progress-track">
            <div className="progress-fill" style={{ width: `${overallProgress.percent}%` }} />
          </div>
        </div>
      )}

      {/* Start Review — only in Topics mode */}
      {!showQuranSection && (
      <div className="space-y-2 mb-4">
        <button
          onClick={handleStartReview}
          disabled={reviewLoading}
          className="dt-ghost-btn w-full px-3 py-2.5 min-h-[44px] text-sm rounded-xl text-left font-semibold disabled:opacity-60 flex items-center gap-2"
        >
          <svg className="w-4 h-4 shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
          </svg>
          {reviewLoading ? "Finding topic\u2026" : "Start Review"}
        </button>
        {reviewError && <p className="text-xs px-1" style={{ color: "var(--tag-rose)" }}>{reviewError}</p>}

        <button
          onClick={() => setShowWordBank(true)}
          className="dt-ghost-btn w-full px-3 py-2.5 min-h-[44px] text-sm rounded-xl text-left font-semibold flex items-center gap-2"
        >
          <svg className="w-4 h-4 shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
          </svg>
          Word Bank
        </button>

        {/* The free tools (Iʿrāb, Conjugator) are always available to a
            signed-in account; their launch switches only gate anonymous
            visitors on the public pages (core/permissions.py). */}
        <button
          onClick={() => setShowIrabTool(true)}
          className="dt-ghost-btn w-full px-3 py-2.5 min-h-[44px] text-sm rounded-xl text-left font-semibold flex items-center gap-2"
        >
          <span className="w-4 h-4 shrink-0 inline-flex items-center justify-center text-base leading-none">◇</span>
          Iʿrāb Tool
        </button>

        <button
          onClick={() => setShowSarfTool(true)}
          className="dt-ghost-btn w-full px-3 py-2.5 min-h-[44px] text-sm rounded-xl text-left font-semibold flex items-center gap-2"
        >
          <span className="w-4 h-4 shrink-0 inline-flex items-center justify-center text-base leading-none">◈</span>
          Conjugator
        </button>

        {(feedEnabled || isAdmin) && !feedButtonHidden && (
        <button
          onClick={openFeed}
          className="dt-ghost-btn w-full px-3 py-2.5 min-h-[44px] text-sm rounded-xl text-left font-semibold flex items-center gap-2"
        >
          <svg className="w-4 h-4 shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M19 20H5a2 2 0 01-2-2V6a2 2 0 012-2h10l6 6v8a2 2 0 01-2 2z" />
            <path strokeLinecap="round" strokeLinejoin="round" d="M8 12h8M8 16h5" />
          </svg>
          Feed
        </button>
        )}

        {isAdmin && (
          <button
            onClick={() => setShowWordBankReview(true)}
            className="dt-ghost-btn w-full px-3 py-2.5 min-h-[44px] text-sm rounded-xl text-left flex items-center gap-2"
          >
            <svg className="w-4 h-4 shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            Review New Words
          </button>
        )}

        {isAdmin && (
          <button
            onClick={() => setShowFeedAdmin(true)}
            className="dt-ghost-btn w-full px-3 py-2.5 min-h-[44px] text-sm rounded-xl text-left flex items-center gap-2"
          >
            <svg className="w-4 h-4 shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M4 21V4m0 1h13l-2.5 4L17 13H4" />
            </svg>
            Feed Moderation
          </button>
        )}

        {isAdmin && (
          <button
            onClick={() => setShowSupportAdmin(true)}
            className="dt-ghost-btn w-full px-3 py-2.5 min-h-[44px] text-sm rounded-xl text-left flex items-center gap-2"
          >
            <svg className="w-4 h-4 shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M18.364 5.636a9 9 0 010 12.728m-12.728 0a9 9 0 010-12.728m2.828 9.9a5 5 0 010-7.072m7.072 0a5 5 0 010 7.072M12 12h.01" />
            </svg>
            Support Requests
          </button>
        )}

        <button
          onClick={() => setShowCreateTopic(true)}
          className="dt-ghost-btn w-full px-3 py-2.5 min-h-[44px] text-sm rounded-xl text-left flex items-center gap-2"
        >
          <svg className="w-4 h-4 shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M12 4v16m8-8H4" />
          </svg>
          Create Topic
        </button>
      </div>
      )}

      {showQuranSection ? (
        <QuranicTopics
          onTopicSelect={(id, name, isBuiltin = false) => {
            setSelectedQuranicTopicId(id);
            setSelectedQuranicTopicIsBuiltin(!!isBuiltin);
            setSelectedTopicId(null); setSelectedTopic(null);
            setSelectedWordId(null); setSelectedWord(null);
            setSelectedQuranicWord(null); setShowQuranicQuiz(false);
            setShowQuiz(false); setShowChat(false); setShowFreeChat(false);
            setBreadcrumb({ fromTopic: name || "Quran", wordText: null, toTopic: null });
            setActiveTab("detail");
          }}
          onProgressUpdate={setQuranicProgress}
          builtinRefreshKey={builtinRefreshKey}
          selectedTopicId={selectedQuranicTopicId}
          selectedTopicIsBuiltin={selectedQuranicTopicIsBuiltin}
        />
      ) : (
        <>
          {topicsLoading && (
            <div className="flex justify-center py-4" role="status" aria-label="Loading topics">
              <span className="w-9 h-9 rounded-full animate-spin border-[3px]" style={{ borderColor: "var(--gold-dim)", borderTopColor: "var(--gold)" }} />
            </div>
          )}
          {topicsError && <p className="text-sm px-1" style={{ color: "var(--tag-rose)" }}>{topicsError}</p>}

          {!topicsLoading && !topicsError && (
            <TopicList
              topics={topics}
              categories={topicCategories}
              selectedCategoryId={selectedCategoryId}
              onSelectCategory={setSelectedCategoryId}
              onCategoriesChanged={handleCategoriesChanged}
              selectedTopicId={selectedTopicId}
              topicProgress={topicProgressMap}
              isAdmin={isAdmin}
              instituteRole={currentUser?.institute?.role || ""}
              onReordered={(reorderedTopics) => {
                setTopics(reorderedTopics);
                // Keep the open topic's header picker in step with a sidebar category change.
                setSelectedTopic((prev) => {
                  const t = prev && reorderedTopics.find((x) => x.id === prev.id);
                  return t && t.category !== prev.category ? { ...prev, category: t.category } : prev;
                });
              }}
              onSelect={(id) => {
                setSelectedTopicId(id);
                setSelectedQuranicTopicId(null); setSelectedQuranicWord(null); setShowQuranicQuiz(false);
                setShowQuiz(false); setShowChat(false); setShowFreeChat(false);
                setSelectedWordId(null); setSelectedWord(null); setWordTopics([]); setWordTopicsError(null);
                const topic = topics.find((t) => t.id === id);
                setBreadcrumb({ fromTopic: topic ? topic.name : null, wordText: null, toTopic: null });
                setActiveTab("detail");
              }}
            />
          )}
        </>
      )}
    </>
  );

  const searchPanelJSX = (
    <>
      <div className="mb-3">
        <GlobalSearch
          isAdmin={isAdmin}
          onSelectWord={handleSearchWord}
          onSelectSentence={handleSearchSentence}
          onSelectTopic={handleSearchTopic}
          onAddWord={(text) => setAddWordModalText(text)}
        />
      </div>
      <WordDetail
        word={selectedWord} occurrence={selectedWordOccurrence} loading={wordLoading} error={wordError}
        onClear={handleClearWord} topicsForWord={wordTopics}
        topicsForWordLoading={wordTopicsLoading} topicsForWordError={wordTopicsError}
        onTopicSelect={(topicId, topicName) => { handleTopicSelectFromWord(topicId, topicName); setActiveTab("detail"); }}
        onFormWordClick={handleFormWordClick} isAdmin={isAdmin}
        onWordUpdate={(updatedWord) => setSelectedWord(updatedWord)}
        currentTopicId={selectedTopicId} currentWordType={selectedWordType} topicOwnedByUser={!!selectedTopic?.is_owner}
        onTopicWordTypeChange={handleTopicWordTypeChange}
      />
    </>
  );

  const BreadcrumbRow = () => (
    <div className="text-sm text-ink-400">
      {breadcrumb.fromTopic && !breadcrumb.wordText && !breadcrumb.toTopic && <span>{breadcrumb.fromTopic}</span>}
      {breadcrumb.fromTopic && breadcrumb.wordText && !breadcrumb.toTopic && (
        <span>{breadcrumb.fromTopic} &rarr; <strong className="text-slate-700">{breadcrumb.wordText}</strong></span>
      )}
      {breadcrumb.fromTopic && breadcrumb.wordText && breadcrumb.toTopic && (
        <span>{breadcrumb.fromTopic} &rarr; <strong className="text-slate-700">{breadcrumb.wordText}</strong> &rarr; {breadcrumb.toTopic}</span>
      )}
    </div>
  );

  const tabClass = (tab) => {
    // "Speaking" (topics) stays highlighted while viewing a topic's detail,
    // since the detail view lives under the Speaking section (no Detail tab).
    const active = activeTab === tab || (tab === "topics" && activeTab === "detail");
    return `flex-1 flex flex-col items-center justify-center py-2 text-xs font-medium transition-colors ${active ? "text-ink-600" : "text-ink-400 hover:text-ink-600"}`;
  };

  const MOBILE_TAB_ORDER = ["topics", "detail", "search"];
  const mobilePanel = (tab) => {
    const ai = MOBILE_TAB_ORDER.indexOf(activeTab);
    const ti = MOBILE_TAB_ORDER.indexOf(tab);
    if (ti === ai) return "translate-x-0";
    return ti < ai ? "-translate-x-full pointer-events-none" : "translate-x-full pointer-events-none";
  };

  // ── PiP drag helpers ────────────────────────────────────────────────────────
  const PIP_W = 240; // fixed widget width (px) — compact on both mobile and desktop

  function initPipPos() {
    const navH = window.innerWidth < 640 ? 56 : 0; // mobile bottom-nav height
    return {
      left: window.innerWidth - PIP_W - 8,
      top: window.innerHeight - 200 - navH - 8,
    };
  }

  function handleMinimize() {
    if (!pipPos) setPipPos(initPipPos());
    setPracticeMinimized(true);
  }

  function handleMaximize() {
    setPracticeMinimized(false);
  }

  // Drag uses window-level listeners with passive:false so e.preventDefault()
  // can suppress vertical scroll on mobile, enabling free 4-directional drag.
  function pipPointerDown(e) {
    if (!pipRef.current) return;
    e.preventDefault();
    const rect = pipRef.current.getBoundingClientRect();
    const startCx = e.clientX;
    const startCy = e.clientY;
    const startLeft = rect.left;
    const startTop = rect.top;

    function onMove(ev) {
      ev.preventDefault(); // prevents scroll from stealing vertical drag
      const dx = ev.clientX - startCx;
      const dy = ev.clientY - startCy;
      const el = pipRef.current;
      const W = window.innerWidth;
      const H = window.innerHeight;
      const w = el ? el.offsetWidth : PIP_W;
      const h = el ? el.offsetHeight : 200;
      setPipPos({
        left: Math.max(8, Math.min(W - w - 8, startLeft + dx)),
        top: Math.max(8, Math.min(H - h - 8, startTop + dy)),
      });
    }

    function onUp() {
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerup", onUp);
    }

    window.addEventListener("pointermove", onMove, { passive: false });
    window.addEventListener("pointerup", onUp);
  }
  // ────────────────────────────────────────────────────────────────────────────

  async function handleLinkQuranVerb(metadataId, senseId) {
    setVerbLinkLoading(true);
    try {
      const verbSense = await linkQuranVerb(metadataId, senseId);
      // Update in-place so UI reflects the new link immediately
      setSelectedQuranicWord(prev => {
        if (!prev) return prev;
        return {
          ...prev,
          word: {
            ...prev.word,
            metadata: { ...prev.word.metadata, verb_sense: verbSense },
            conjugation_match: null,
          },
        };
      });
    } catch (e) {
      console.error("Link failed", e);
    } finally {
      setVerbLinkLoading(false);
    }
  }

  async function handleUnlinkQuranVerb(metadataId, conjMatch) {
    setVerbLinkLoading(true);
    try {
      await unlinkQuranVerb(metadataId);
      setSelectedQuranicWord(prev => {
        if (!prev) return prev;
        return {
          ...prev,
          word: {
            ...prev.word,
            metadata: { ...prev.word.metadata, verb_sense: null },
            // Restore the conjugation_match so admin can re-link if desired
            conjugation_match: conjMatch || null,
          },
        };
      });
    } catch (e) {
      console.error("Unlink failed", e);
    } finally {
      setVerbLinkLoading(false);
    }
  }

  async function handleSaveMorphologyEdit() {
    const meta = selectedQuranicWord?.word?.metadata;
    if (!meta?.id) return;
    setMorphologySaving(true);
    try {
      const result = await patchQuranWordMetadata(meta.id, morphologyEditValues);
      setSelectedQuranicWord(prev => prev ? {
        ...prev,
        word: { ...prev.word, metadata: result.metadata },
      } : prev);
      setMorphologySaveResult(result);
      setEditingMorphology(false);
    } catch (e) {
      console.error("Morphology save failed", e);
      alert(e.message || "Failed to save corrections");
    } finally {
      setMorphologySaving(false);
    }
  }

  async function handleRevertMorphologyFields(fields) {
    const meta = selectedQuranicWord?.word?.metadata;
    if (!meta?.id) return;
    setMorphologySaving(true);
    try {
      const result = await patchQuranWordMetadata(meta.id, { revert_fields: fields });
      setSelectedQuranicWord(prev => prev ? {
        ...prev,
        word: { ...prev.word, metadata: result.metadata },
      } : prev);
      setMorphologySaveResult(result);
    } catch (e) {
      console.error("Morphology revert failed", e);
      alert(e.message || "Failed to revert");
    } finally {
      setMorphologySaving(false);
    }
  }

  return (
    <>
    <Routes>
      {/* /majlis — the social feed as a full page with its own shell. */}
      <Route
        path="/majlis"
        element={
          <MajlisPage
            currentUser={currentUser}
            isAdmin={isAdmin}
            onOpenTyping={() => { navigate("/"); setShowTyping(true); }}
            onOpenAdmin={() => setShowFeedAdmin(true)}
            onSeen={() => setFeedUnread(0)}
          />
        }
      />
      {/* /majlis/submit — the full-page composer with the live fuṣḥā checker. */}
      <Route
        path="/majlis/submit"
        element={
          <MajlisSubmitPage
            currentUser={currentUser}
            isAdmin={isAdmin}
            onOpenTyping={() => { navigate("/"); setShowTyping(true); }}
            onOpenAdmin={() => setShowFeedAdmin(true)}
          />
        }
      />
      {/* Everything else: the existing state-driven shell, unchanged. */}
      <Route path="*" element={
    <div className="dark-scope flex flex-col h-screen font-sans" style={{ background: "var(--ink)", color: "var(--parchment)" }}>
      {/* First-run welcome: onboarded_at is strictly null only for new signups
          (the field must be present — stale cached users lack it entirely).
          Rendered on top of the shell, not as an early return, so topics load
          underneath and the CTA can select the first one. */}
      {currentUser && currentUser.onboarded_at === null && "onboarded_at" in currentUser && (
        <FirstRunWelcome
          user={currentUser}
          onGoQuran={() => {
            setShowQuranSection(true);
            setSelectedTopicId(null); setSelectedTopic(null);
            setSelectedWordId(null); setSelectedWord(null);
            setActiveTab("topics");
          }}
          onGoFirstTopic={() => {
            setShowQuranSection(false);
            if (topics.length > 0) setSelectedTopicId(topics[0].id);
            setSelectedWordId(null); setSelectedWord(null);
            setActiveTab("detail");
          }}
          onDismiss={() =>
            setCurrentUser((prev) => (prev ? { ...prev, onboarded_at: new Date().toISOString() } : prev))
          }
        />
      )}
      {/* Once-a-day goal greeting — only for users who chose a daily goal,
          and never on the same day as the first-run welcome (it marks the
          day key on dismissal). */}
      <DailyGoalReminder
        minutes={currentUser?.daily_goal_minutes}
        enabled={!!currentUser && currentUser.onboarded_at != null}
      />
      {/* Monthly AI-credit notice — invisible until metering reaches soft/hard */}
      <AiCreditBanner />
      {/* Our free trial is not a Stripe subscription, so Stripe sends no
          "trial ending" mail — this is the only warning they get. */}
      <TrialEndingBanner user={currentUser} />
      {/* Unified Top Bar */}
      <header className="shrink-0 flex items-center justify-between gap-3 flex-wrap px-4 py-3 bg-parchment-50/90 backdrop-blur sticky top-0 z-20 shadow-soft">
        <div className="flex items-center gap-3 min-w-0 order-1">
          <div className="flex flex-col items-center shrink-0 leading-none select-none">
            <span className="font-arabic-display font-bold text-3xl sm:text-4xl leading-none" style={{ color: "var(--gold-bright)" }} dir="rtl">
              مراجعة
            </span>
            <span className="text-[0.55rem] sm:text-[0.6rem] font-semibold uppercase tracking-[0.2em] sm:tracking-[0.28em] mt-1.5" style={{ color: "var(--gold-dim)" }}>
              murājaʿa
            </span>
          </div>
          {/* Subtle institute affiliation — mirrors the tagline's small-caps voice */}
          {currentUser?.institute?.name && (
            <div
              className="hidden sm:flex items-center gap-2.5 min-w-0 select-none"
              title={`Your account is part of ${currentUser.institute.name}`}
            >
              <span
                className="h-7 w-px shrink-0"
                style={{ background: "var(--gold-dim)", opacity: 0.35 }}
              />
              <span
                className="text-[0.65rem] font-semibold uppercase tracking-[0.18em] truncate"
                style={{ color: "var(--gold-dim)" }}
              >
                {currentUser.institute.name}
              </span>
            </div>
          )}
        </div>

        <div className="flex items-center gap-1 sm:gap-2 shrink-0 order-3 basis-full justify-center sm:order-2 sm:basis-auto sm:justify-start sm:ms-auto">
          {/* Typing tutor — learn the Arabic keyboard, letters through ḥarakāt */}
          <button
            onClick={() => setShowTyping(true)}
            title="Typing — learn to type Arabic with ḥarakāt"
            className="p-2 rounded-lg text-ink-400 hover:text-ink-600 hover:bg-ink-50 transition-colors hidden sm:block"
            aria-label="Typing"
          >
            <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
              <rect x="2" y="6" width="20" height="12" rx="2" strokeLinecap="round" strokeLinejoin="round" />
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 10h.01M10 10h.01M14 10h.01M18 10h.01M8 14h8" />
            </svg>
          </button>

          {/* Feed — the fuṣḥā-only social feed (dark-launched via feed_enabled;
              admins always see it for pre-launch testing) */}
          {(feedEnabled || isAdmin) && !feedButtonHidden && (
          <button
            onClick={openFeed}
            title="Feed — share what you write in fuṣḥā"
            className="relative p-2 rounded-lg text-ink-400 hover:text-ink-600 hover:bg-ink-50 transition-colors"
            aria-label="Feed"
          >
            <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M19 20H5a2 2 0 01-2-2V6a2 2 0 012-2h10l6 6v8a2 2 0 01-2 2z" />
              <path strokeLinecap="round" strokeLinejoin="round" d="M8 12h8M8 16h5" />
            </svg>
            {feedUnread > 0 && (
              <span
                className="absolute top-1 right-1 w-2 h-2 rounded-full"
                style={{ background: "var(--gold)" }}
                aria-label={`${feedUnread} new feed interactions`}
              />
            )}
          </button>
          )}

          {/* Free Chat — free-form Arabic conversation with the AI tutor */}
          <button
            onClick={() => { setSelectedWordId(null); setActiveTab("detail"); setShowFreeChat(true); }}
            title="Free Chat — free-form Arabic conversation"
            className="p-2 rounded-lg text-ink-400 hover:text-ink-600 hover:bg-ink-50 transition-colors"
            aria-label="Free Chat"
          >
            <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z" />
            </svg>
          </button>

          <button
            onClick={() => setShowSpeakingHelper(true)}
            title="Speaking Helper — say it in Arabic during a live conversation"
            className="p-2 rounded-lg text-ink-400 hover:text-ink-600 hover:bg-ink-50 transition-colors"
            aria-label="Speaking Helper"
          >
            <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M8 10h.01M12 10h.01M16 10h.01M21 12a8 8 0 01-11.3 7.3L3 21l1.7-6.7A8 8 0 1121 12z" />
            </svg>
          </button>

          <button
            onClick={() => setShowLeaderboard(true)}
            title="Leaderboard"
            className="p-2 rounded-lg text-ink-400 hover:text-ink-600 hover:bg-ink-50 transition-colors"
            aria-label="Leaderboard"
          >
            <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M8 21h8M12 17v4M4 7h16M4 7l2-4h12l2 4M4 7v5a4 4 0 004 4h8a4 4 0 004-4V7" />
            </svg>
          </button>

          {(!practiceCallsButtonHidden || isAdmin) && (
          <button
            onClick={() => setShowPractice(true)}
            title="Practice Calls"
            className="p-2 rounded-lg text-ink-400 hover:text-ink-600 hover:bg-ink-50 transition-colors"
            aria-label="Practice Calls"
          >
            <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M15 10l4.553-2.069A1 1 0 0121 8.87v6.26a1 1 0 01-1.447.894L15 14M4 8h11a2 2 0 012 2v4a2 2 0 01-2 2H4a2 2 0 01-2-2v-4a2 2 0 012-2z" />
            </svg>
          </button>
          )}

          {currentUser?.institute?.role === "admin" && (
            <button
              onClick={() => setShowInstitutePanel(true)}
              title="Institute Admin"
              className="p-2 rounded-lg text-ink-400 hover:text-ink-600 hover:bg-ink-50 transition-colors"
              aria-label="Institute Admin"
            >
              <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M12 14l9-5-9-5-9 5 9 5z" />
                <path strokeLinecap="round" strokeLinejoin="round" d="M12 14l6.16-3.422a12.083 12.083 0 01.665 6.479A11.952 11.952 0 0012 20.055a11.952 11.952 0 00-6.824-2.998 12.078 12.078 0 01.665-6.479L12 14z" />
              </svg>
            </button>
          )}

          {isAdmin && (
            <button
              onClick={() => setShowAdminSettings(true)}
              title="App Settings (Admin)"
              className="p-2 rounded-lg text-ink-400 hover:text-ink-600 hover:bg-ink-50 transition-colors"
              aria-label="App Settings"
            >
              <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
              </svg>
            </button>
          )}

        </div>

        {/* Avatar — top-right beside the logo on phones (order-2); rejoins
            the end of the single row on sm+ (order-3). */}
        <div className="order-2 sm:order-3 shrink-0">
          <UserProfileMenu
            currentUser={currentUser}
            onLogout={handleLogout}
            onUserUpdate={(updated) => setCurrentUser(updated)}
          />
        </div>
      </header>

      {/* Gold diamond divider under the top bar (replaces the plain border) */}
      <DiamondDivider className="shrink-0 px-6 py-1" />

      {/* Practice-call email action result banner */}
      {practiceResultBanner && (
        <div className="shrink-0 flex items-center justify-between gap-3 px-4 py-2.5 bg-ink-50 border-b border-ink-200 text-sm text-ink-700">
          <span>{practiceResultBanner}</span>
          <button
            onClick={() => setPracticeResultBanner(null)}
            className="shrink-0 text-emerald-600 hover:text-emerald-800 font-bold leading-none"
            aria-label="Dismiss"
          >✕</button>
        </div>
      )}

      {/* Desktop layout (md+) */}
      <div className="hidden md:flex flex-1 overflow-hidden">
        <aside className="dark-scope w-60 lg:w-72 flex flex-col overflow-hidden shrink-0" style={{ background: "var(--ink)" }}>
          {/* Search bar at TOP of sidebar on desktop */}
          <div className="p-3 shrink-0">
            <GlobalSearch
              isAdmin={isAdmin}
              onSelectWord={handleSearchWord}
              onSelectSentence={handleSearchSentence}
              onSelectTopic={handleSearchTopic}
              onAddWord={(text) => setAddWordModalText(text)}
            />
          </div>
          <div className="flex-1 overflow-y-auto p-4 scroll-elegant">
            {SidebarContent()}
          </div>
        </aside>

        {/* Vertical diamond divider between sidebar and main */}
        <DiamondDivider vertical className="shrink-0" />

        <main className="flex-1 flex flex-col overflow-hidden">
          {/* Toolbar row — only rendered when it has something to show (a
              breadcrumb trail or the Quranic quiz launcher). Collapsing it when
              empty lets the content sit directly under the top-bar divider.
              It must stay mounted for the whole time a topic is open, not just
              while a word is selected: this row sits ABOVE the scrolling
              content, so mounting it on word-select (and unmounting it when the
              next word clears the selection) shoved the scenario text down and
              back up — the "scroll twitch" after opening Word Detail from the
              word tooltip's ⓘ button. Truncating to a single line keeps its
              height fixed as the trail grows from "Topic" to "Topic → word". */}
          {!showQuiz && !showAiQuiz && !showChat && !showFreeChat && !showQuranicQuiz && !selectedTopicIsGrammarLesson &&
            ((selectedTopicId && breadcrumb.fromTopic) || breadcrumb.wordText || selectedQuranicTopicId) && (
            <div className="shrink-0 px-6 py-3 bg-parchment-50/70 flex items-center justify-between gap-3">
              <div className="flex-1 min-w-0 truncate">
                <BreadcrumbRow />
              </div>
              {/* Quranic topics have no guided journey, so keep their quiz
                  launcher here; regular topics start the quiz from the
                  guided journey's Quiz step. */}
              {selectedQuranicTopicId && (
                <button
                  onClick={() => setShowQuranicQuiz(true)}
                  className="px-3 py-1.5 min-h-[44px] text-sm rounded-xl bg-gold-500 text-white hover:bg-gold-600 shrink-0 font-semibold shadow-soft transition-colors"
                >
                  Start Quiz
                </button>
              )}
            </div>
          )}

          <div className="flex flex-1 min-h-0">
            <div className="flex-1 overflow-y-auto p-6 scroll-elegant">
              {topicError && <p className="text-sm text-red-600">{topicError}</p>}
              {showAiQuiz ? (
                <AiTopicQuiz layout="desktop"
                  topicId={selectedTopicId} topicName={selectedTopic?.name}
                  onExit={() => { setShowAiQuiz(false); refreshTopicDetail(); loadProgress(); }}
                  onComplete={() => { loadProgress(); refreshTopicDetail(); }}
                  onProgress={loadProgress}
                />
              ) : showQuiz ? (
                <TopicQuiz
                  topicId={selectedTopicId} quizMode={quizMode}
                  topicName={selectedTopic?.name}
                  topicBestScore={topicProgressMap[selectedTopicId]?.best_score || 0}
                  onExit={() => { setShowQuiz(false); refreshTopicDetail(); }}
                  onComplete={() => { loadProgress(); refreshTopicDetail(); }}
                  onProgress={loadProgress}
                />
              ) : showFreeChat ? (
                <RolePlayView
                  isAdmin={isAdmin}
                  onExit={() => setShowFreeChat(false)}
                  freeChat
                  onConvertedToTopic={async (topicId) => {
                    setShowFreeChat(false);
                    await reloadTopics();
                    setSelectedTopicId(topicId);
                    setActiveTab("detail");
                  }}
                />
              ) : showChat ? (
                <RolePlayView topic={selectedTopic} isAdmin={isAdmin} onExit={() => { setShowChat(false); refreshTopicDetail(); }} onRolePlayCompleted={handleRolePlayCompleted} />
              ) : selectedQuranicTopicId ? (
                <QuranicTopicDetail
                  topicId={selectedQuranicTopicId}
                  isBuiltin={selectedQuranicTopicIsBuiltin}
                  onBack={() => { setSelectedQuranicTopicId(null); setSelectedQuranicTopicIsBuiltin(false); setSelectedQuranicWord(null); setShowQuranicQuiz(false); }}
                  onWordDetail={(word, verseKey, verseTranslation) => setSelectedQuranicWord({ word, verseKey, verseTranslation })}
                  quizActive={showQuranicQuiz}
                  onQuizExit={() => setShowQuranicQuiz(false)}
                  onBuiltinComplete={() => setBuiltinRefreshKey((k) => k + 1)}
                  onOpenIrab={openQuranicIrab}
                />
              ) : selectedTopicIsGrammarLesson ? (
                <GrammarLessonView
                  topicId={selectedTopicId}
                  onBack={() => { setSelectedTopicId(null); setSelectedTopic(null); }}
                  onProgress={loadProgress}
                  isAdmin={isAdmin} categories={topicCategories}
                  categoryId={topics.find((t) => t.id === selectedTopicId)?.category ?? null}
                  onCategoryChanged={handleTopicCategoryChanged}
                />
              ) : (
                <TopicDetail
                  topic={selectedTopic} loading={topicLoading} focusTurn={focusTurn}
                  onWordSelect={handleWordSelect} onClearWord={handleClearWord}
                  selectedWordId={selectedWordId} isAdmin={isAdmin}
                  onTopicUpdate={(updatedTopic) => setSelectedTopic(updatedTopic)}
                  categories={topicCategories} onCategoryChanged={handleTopicCategoryChanged}
                  onTurnSaved={refreshTopicDetail}
                  onStartChat={() => { setSelectedWordId(null); setShowChat(true); }}
                  onStartQuiz={() => { setSelectedWordId(null); setShowChat(false); setShowFreeChat(false); setQuizMode("learn"); setShowQuiz(true); }}
                  onStartAiQuiz={() => { setSelectedWordId(null); setShowChat(false); setShowFreeChat(false); setShowQuiz(false); setShowAiQuiz(true); }}
                  onWordTypeChange={handleTopicWordTypeChange}
                  onTopicDeleted={async () => {
                    setSelectedTopicId(null);
                    setSelectedTopic(null);
                    await reloadTopics();
                    loadProgress();
                  }}
                />
              )}
            </div>

            {!showQuiz && !showAiQuiz && !showChat && !showFreeChat && !selectedTopicIsGrammarLesson && (
              <DiamondDivider vertical className="shrink-0 hidden lg:flex" />
            )}
            {!showQuiz && !showAiQuiz && !showChat && !showFreeChat && !selectedTopicIsGrammarLesson && (
              <div ref={wordDetailPanelRef} className="dark-scope hidden lg:flex lg:flex-col w-96 overflow-y-auto shrink-0 scroll-elegant" style={{ background: "var(--ink)" }}>
                <div className="p-4">
                  {/* On the Listening step of a video topic, TopicDetail portals
                      the synced player here so it sits above Word Detail. */}
                  <div id="topic-listening-video-slot" />
                  <h2 className="text-base font-cormorant font-semibold mb-3" style={{ color: "var(--parchment)" }}>Word Detail</h2>
                  {selectedQuranicTopicId ? (
                    selectedQuranicWord ? (
                      <div>
                        <div className="flex items-center justify-between mb-4">
                          <div>
                            <span className="text-xs font-mono text-slate-400">{selectedQuranicWord.word.location || selectedQuranicWord.verseKey}</span>
                            {selectedQuranicWord.word.page_number && (
                              <span className="ml-2 text-xs text-slate-400">· Page {selectedQuranicWord.word.page_number}</span>
                            )}
                          </div>
                          <button onClick={() => setSelectedQuranicWord(null)} className="text-slate-400 hover:text-slate-600 text-lg leading-none">&times;</button>
                        </div>
                        <p className="text-5xl font-arabic text-slate-800 text-center mb-2" dir="rtl">{selectedQuranicWord.word.arabic}</p>
                        {selectedQuranicWord.word.transliteration && (
                          <p className="text-sm text-slate-500 text-center italic mb-1">{selectedQuranicWord.word.transliteration}</p>
                        )}
                        {selectedQuranicWord.word.meaning && (
                          <p className="text-lg font-medium text-slate-800 text-center mb-4">{selectedQuranicWord.word.meaning}</p>
                        )}
                        {selectedQuranicWord.word.audio_url && (
                          <button
                            onClick={() => { const a = new Audio(selectedQuranicWord.word.audio_url); a.play(); }}
                            className="w-full py-2 rounded-lg dt-ghost-btn text-sm transition-colors flex items-center justify-center gap-2 mb-4"
                          >
                            <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24"><path d="M8 5v14l11-7z"/></svg>
                            Play Word
                          </button>
                        )}
                        {selectedQuranicWord.verseTranslation && (
                          <div className="mt-2 p-3 bg-[var(--gold-bg)] rounded-xl border border-[var(--panel-line)]">
                            <p className="text-xs font-semibold text-amber-700 mb-1">Verse {selectedQuranicWord.verseKey}</p>
                            <p className="text-sm text-dt-parchment leading-relaxed">{selectedQuranicWord.verseTranslation.replace(/<[^>]+>/g, "")}</p>
                          </div>
                        )}
                        {/* Word metadata from imported curriculum */}
                        {selectedQuranicWord.word.metadata && (
                          <div className="mt-3 p-3 surface-glass rounded-xl">
                            {/* Header row */}
                            <div className="flex items-baseline justify-between mb-2">
                              <p className="text-xs font-semibold text-slate-600">Morphological Analysis</p>
                              <div className="flex items-center gap-2">
                                {isAdmin && !editingMorphology && (
                                  <button
                                    onClick={() => {
                                      const m = selectedQuranicWord.word.metadata;
                                      setMorphologyEditValues({
                                        grammar_label_ar: m.grammar_label_ar || "",
                                        morphology_summary: m.morphology_summary || "",
                                        frequency_total: m.frequency_total ?? "",
                                        lemma: m.lemma || "",
                                        root: m.root || "",
                                      });
                                      setEditingMorphology(true);
                                      setMorphologySaveResult(null);
                                    }}
                                    className="text-[10px] text-slate-400 hover:text-slate-600 flex items-center gap-0.5"
                                  >✏ Edit</button>
                                )}
                                <a href="http://corpus.quran.com" target="_blank" rel="noopener noreferrer" className="text-[10px] text-slate-400 hover:text-slate-600 hover:underline">Quranic Arabic Corpus</a>
                              </div>
                            </div>

                            {/* Corrections banner — transient feedback after save/revert */}
                            {morphologySaveResult && morphologySaveResult.changed?.length > 0 && (
                              <div className="mb-2 p-2 rounded-lg bg-emerald-50 border border-emerald-200 text-xs">
                                <p className="font-semibold text-emerald-700 mb-1">
                                  {morphologySaveResult.changed.some(c => c.reverted) ? "↺ Reverted to original:" : "✓ Saved corrections:"}
                                </p>
                                {morphologySaveResult.changed.map(c => (
                                  <div key={c.field} className="text-emerald-800">
                                    <span className="font-medium capitalize">{c.field.replace(/_/g, " ")}</span>:{" "}
                                    <span className="line-through text-slate-400">{String(c.original ?? "—")}</span>{" → "}
                                    <span>{String(c.corrected ?? "—")}</span>
                                  </div>
                                ))}
                              </div>
                            )}
                            {editingMorphology ? (
                              /* ── Edit form ── */
                              <div className="space-y-2 text-sm">
                                {[
                                  { key: "grammar_label_ar", label: "Type (Arabic)", dir: "rtl" },
                                  { key: "morphology_summary", label: "Morphology (raw)", dir: "ltr" },
                                  { key: "frequency_total", label: "Frequency in Quran", dir: "ltr", type: "number" },
                                  { key: "lemma", label: "Lemma (BW)", dir: "ltr" },
                                  { key: "root", label: "Root (BW)", dir: "ltr" },
                                ].map(({ key, label, dir, type }) => (
                                  <div key={key}>
                                    <label className="block text-xs text-slate-500 mb-0.5">{label}</label>
                                    <input
                                      type={type || "text"}
                                      dir={dir}
                                      value={morphologyEditValues[key] ?? ""}
                                      onChange={e => setMorphologyEditValues(v => ({ ...v, [key]: e.target.value }))}
                                      className="w-full text-sm border border-slate-300 rounded-lg px-2 py-1 focus:outline-none focus:border-[var(--gold)] font-mono"
                                    />
                                  </div>
                                ))}
                                <div className="flex gap-2 pt-1">
                                  <button
                                    onClick={handleSaveMorphologyEdit}
                                    disabled={morphologySaving}
                                    className="flex-1 py-1.5 text-xs rounded-lg bg-dt-gold-bright text-dt-ink hover:brightness-105 disabled:opacity-50"
                                  >{morphologySaving ? "Saving…" : "Save"}</button>
                                  <button
                                    onClick={() => setEditingMorphology(false)}
                                    className="flex-1 py-1.5 text-xs rounded-lg border border-slate-300 text-slate-600 hover:bg-slate-100"
                                  >Cancel</button>
                                </div>
                              </div>
                            ) : (
                              /* ── Display mode ── */
                              <>
                                <dl className="space-y-1.5 text-sm">
                                  {(parsedMorphology?.type_ar || selectedQuranicWord.word.metadata.grammar_label_ar) && (
                                    <div className="flex justify-between">
                                      <dt className="text-slate-500">Type</dt>
                                      <dd className="font-arabic text-slate-800 font-medium">
                                        {parsedMorphology?.type_ar || selectedQuranicWord.word.metadata.grammar_label_ar}
                                      </dd>
                                    </div>
                                  )}
                                  {parsedMorphology?.form_ar && (
                                    <div className="flex justify-between">
                                      <dt className="text-slate-500">Form</dt>
                                      <dd className="font-arabic text-slate-800">{parsedMorphology.form_ar}</dd>
                                    </div>
                                  )}
                                  {parsedMorphology?.number_ar && (
                                    <div className="flex justify-between">
                                      <dt className="text-slate-500">Number</dt>
                                      <dd className="font-arabic text-slate-800">{parsedMorphology.number_ar}</dd>
                                    </div>
                                  )}
                                  {parsedMorphology?.gender_ar && (
                                    <div className="flex justify-between">
                                      <dt className="text-slate-500">Gender</dt>
                                      <dd className="font-arabic text-slate-800">{parsedMorphology.gender_ar}</dd>
                                    </div>
                                  )}
                                  {(correctedCase || parsedMorphology?.case_ar) && (
                                    <div className="flex justify-between items-center">
                                      <dt className="text-slate-500">Case</dt>
                                      <dd className="flex items-center gap-1.5">
                                        <span className="font-arabic text-slate-800 font-medium">
                                          {correctedCase ? correctedCase.case_label_ar : parsedMorphology.case_ar}
                                        </span>
                                        {correctedCase?.case_source === "derived" && (
                                          <span className="text-xs text-slate-400">(corrected)</span>
                                        )}
                                      </dd>
                                    </div>
                                  )}
                                  {parsedMorphology?.root_ar && (
                                    <div className="flex justify-between">
                                      <dt className="text-slate-500">Root</dt>
                                      <dd className="font-arabic text-slate-800">{parsedMorphology.root_ar}</dd>
                                    </div>
                                  )}
                                  {selectedQuranicWord.word.metadata.frequency_total != null && (
                                    <div className="flex justify-between">
                                      <dt className="text-slate-500">Frequency in Quran</dt>
                                      <dd className="text-slate-800 font-arabic">{selectedQuranicWord.word.metadata.frequency_total} مرة</dd>
                                    </div>
                                  )}
                                </dl>
                                {selectedQuranicWord.word.metadata.morphology_summary && (
                                  <div className="mt-2.5 pt-2 border-t border-slate-200">
                                    <button
                                      onClick={() => setShowAdvancedMorphology(v => !v)}
                                      className="text-xs text-slate-400 hover:text-slate-600 transition-colors"
                                    >
                                      {showAdvancedMorphology ? "Hide Details ▴" : "Advanced ▾"}
                                    </button>
                                    {showAdvancedMorphology && (
                                      <>
                                        <dl className="mt-2 space-y-1.5 text-xs text-slate-500">
                                          {selectedQuranicWord.word.metadata.lemma && (
                                            <div className="flex justify-between">
                                              <dt>Lemma</dt>
                                              <dd className="font-mono">{selectedQuranicWord.word.metadata.lemma}</dd>
                                            </div>
                                          )}
                                          {selectedQuranicWord.word.metadata.root && (
                                            <div className="flex justify-between">
                                              <dt>Root (BW)</dt>
                                              <dd className="font-mono">{selectedQuranicWord.word.metadata.root}</dd>
                                            </div>
                                          )}
                                          <div className="flex flex-col gap-0.5 pt-0.5">
                                            <dt>Morphology</dt>
                                            <dd className="font-mono break-all leading-relaxed">
                                              {selectedQuranicWord.word.metadata.morphology_summary}
                                            </dd>
                                          </div>
                                        </dl>
                                        {/* Overrides indicator — admin only */}
                                        {isAdmin && !editingMorphology && selectedQuranicWord.word.metadata.morphology_overrides && Object.keys(selectedQuranicWord.word.metadata.morphology_overrides).length > 0 && (
                                          <div className="mt-2 p-2 rounded-lg bg-amber-50 border border-amber-200 text-xs">
                                            <div className="flex items-center justify-between mb-1">
                                              <p className="font-semibold text-amber-700">⚠ Manually corrected:</p>
                                              <button
                                                onClick={() => handleRevertMorphologyFields(Object.keys(selectedQuranicWord.word.metadata.morphology_overrides))}
                                                disabled={morphologySaving}
                                                className="text-[10px] text-amber-600 hover:text-amber-800 underline disabled:opacity-50"
                                              >Revert all</button>
                                            </div>
                                            {Object.entries(selectedQuranicWord.word.metadata.morphology_overrides).map(([field, v]) => (
                                              <div key={field} className="flex items-center justify-between gap-1 text-amber-800">
                                                <span>
                                                  <span className="font-medium capitalize">{field.replace(/_/g, " ")}</span>:{" "}
                                                  <span className="line-through text-slate-400">{String(v.original ?? "—")}</span>{" → "}
                                                  <span>{String(v.current ?? "—")}</span>
                                                </span>
                                                <button
                                                  onClick={() => handleRevertMorphologyFields([field])}
                                                  disabled={morphologySaving}
                                                  className="shrink-0 text-[10px] text-amber-600 hover:text-amber-800 underline disabled:opacity-50"
                                                >↺</button>
                                              </div>
                                            ))}
                                          </div>
                                        )}
                                      </>
                                    )}
                                  </div>
                                )}
                              </>
                            )}
                          </div>
                        )}

                        {/* Verb conjugation link panel (all users see linked verb; admin can link/unlink) */}
                        {selectedQuranicWord.word.metadata && (
                          <QuranVerbLinkPanel
                            word={selectedQuranicWord.word}
                            loading={verbLinkLoading}
                            isAdmin={isAdmin}
                            onLink={(senseId) => handleLinkQuranVerb(selectedQuranicWord.word.metadata.id, senseId)}
                            onUnlink={() => handleUnlinkQuranVerb(selectedQuranicWord.word.metadata.id, selectedQuranicWord.word.conjugation_match)}
                          />
                        )}
                      </div>
                    ) : (
                      <p className="text-sm text-slate-400">Tap a word in the verse, then press ℹ to see details here.</p>
                    )
                  ) : (
                    <WordDetail
                      word={selectedWord} occurrence={selectedWordOccurrence} loading={wordLoading} error={wordError}
                      onClear={handleClearWord} topicsForWord={wordTopics}
                      topicsForWordLoading={wordTopicsLoading} topicsForWordError={wordTopicsError}
                      onTopicSelect={handleTopicSelectFromWord} onFormWordClick={handleFormWordClick}
                      isAdmin={isAdmin} onWordUpdate={(updatedWord) => setSelectedWord(updatedWord)}
                      currentTopicId={selectedTopicId} currentWordType={selectedWordType} topicOwnedByUser={!!selectedTopic?.is_owner}
                      onTopicWordTypeChange={handleTopicWordTypeChange}
                    />
                  )}
                </div>
              </div>
            )}
          </div>
        </main>
      </div>

      {/* Mobile layout (< md) */}
      {/* overflow-clip (not overflow-hidden): the sliding panels are positioned
          off-screen with translate-x-full, which doubles this element's scroll
          width. overflow-hidden still leaves it a scroll container, so a focus()/
          scrollIntoView() from an opening panel (e.g. Free Chat) scrolls it
          horizontally and reveals the wrong (search) panel. overflow-clip is not
          a scroll container, so it can't be scrolled programmatically. */}
      <div className="md:hidden flex-1 min-h-0 relative overflow-clip">
        <div className={`dark-scope absolute inset-0 overflow-y-auto pb-16 p-4 transition-transform duration-300 ease-in-out ${mobilePanel("topics")}`} style={{ background: "var(--ink)" }}>
          {SidebarContent()}
        </div>

        <div className={`absolute inset-0 overflow-y-auto pb-16 p-4 transition-transform duration-300 ease-in-out ${mobilePanel("detail")}`}>
          {/* Mobile header: back + breadcrumb + compact icon buttons */}
          {!showQuranicQuiz && (
          <div className="mb-4 flex items-center gap-2 min-w-0">
            {/* Back to the topic list. During an activity (quiz/chat) that has
                its own exit, this is hidden to avoid a confusing second back. */}
            {!showQuiz && !showAiQuiz && !showChat && !showFreeChat && (
              <button
                onClick={() => setActiveTab("topics")}
                aria-label="Back to topics"
                title="Back to topics"
                className="shrink-0 -ml-1 flex items-center justify-center w-9 h-9 rounded-xl text-ink-500 hover:bg-parchment-200 active:bg-parchment-300 transition-colors"
              >
                <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
                </svg>
              </button>
            )}
            <div
              className="flex-1 min-w-0 truncate text-sm text-slate-500"
              title={[breadcrumb.fromTopic, breadcrumb.wordText, breadcrumb.toTopic].filter(Boolean).join(" → ")}
            >
              <BreadcrumbRow />
            </div>
            {!showQuiz && !showAiQuiz && !showChat && !showFreeChat && !showQuranicQuiz && (
              <div className="flex items-center gap-1 shrink-0">
                {/* Free Chat icon button — hidden in quranic mode */}
                {!selectedQuranicTopicId && (
                  <button
                    onClick={() => { setSelectedWordId(null); setShowFreeChat(true); }}
                    title="Free Chat"
                    className="flex items-center justify-center w-10 h-10 rounded-xl bg-ink-600 text-parchment-50 hover:bg-ink-700 active:bg-ink-800 shadow-soft transition-colors"
                    aria-label="Free Chat"
                  >
                    <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                    </svg>
                  </button>
                )}
                {/* Quiz icon button — Quranic topics only; regular topics start
                    the quiz from the guided journey's Quiz step. */}
                {selectedQuranicTopicId && (
                  <button
                    onClick={() => setShowQuranicQuiz(true)}
                    title="Start Quiz"
                    className="flex items-center justify-center w-10 h-10 rounded-xl bg-gold-500 text-white hover:bg-gold-600 active:bg-gold-600 shadow-soft transition-colors"
                    aria-label="Start Quiz"
                  >
                    <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                    </svg>
                  </button>
                )}
              </div>
            )}
          </div>
          )}

          {topicError && <p className="text-sm text-red-600">{topicError}</p>}
          {showAiQuiz ? (
            <AiTopicQuiz layout="mobile"
              topicId={selectedTopicId} topicName={selectedTopic?.name}
              onExit={() => { setShowAiQuiz(false); refreshTopicDetail(); loadProgress(); }}
              onComplete={() => { loadProgress(); refreshTopicDetail(); }}
              onProgress={loadProgress}
            />
          ) : showQuiz ? (
            <TopicQuiz
              topicId={selectedTopicId} quizMode={quizMode}
              topicName={selectedTopic?.name}
              topicBestScore={topicProgressMap[selectedTopicId]?.best_score || 0}
              onExit={() => { setShowQuiz(false); refreshTopicDetail(); }}
              onComplete={() => { loadProgress(); refreshTopicDetail(); }}
              onProgress={loadProgress}
            />
          ) : showFreeChat ? (
            <RolePlayView
              isAdmin={isAdmin}
              onExit={() => setShowFreeChat(false)}
              freeChat
              onConvertedToTopic={async (topicId) => {
                setShowFreeChat(false);
                await reloadTopics();
                setSelectedTopicId(topicId);
                setActiveTab("detail");
              }}
            />
          ) : showChat ? (
            <RolePlayView topic={selectedTopic} isAdmin={isAdmin} onExit={() => { setShowChat(false); refreshTopicDetail(); }} onRolePlayCompleted={handleRolePlayCompleted} />
          ) : selectedQuranicTopicId ? (
            <QuranicTopicDetail
              topicId={selectedQuranicTopicId}
              isBuiltin={selectedQuranicTopicIsBuiltin}
              onBack={() => { setSelectedQuranicTopicId(null); setSelectedQuranicTopicIsBuiltin(false); setSelectedQuranicWord(null); setShowQuranicQuiz(false); }}
              onWordDetail={(word, verseKey, verseTranslation) => { setSelectedQuranicWord({ word, verseKey, verseTranslation }); setActiveTab("search"); }}
              quizActive={showQuranicQuiz}
              onQuizExit={() => setShowQuranicQuiz(false)}
              onBuiltinComplete={() => setBuiltinRefreshKey((k) => k + 1)}
              onOpenIrab={openQuranicIrab}
            />
          ) : selectedTopicIsGrammarLesson ? (
            <GrammarLessonView
              topicId={selectedTopicId}
              onBack={() => { setSelectedTopicId(null); setSelectedTopic(null); setActiveTab("topics"); }}
              onProgress={loadProgress}
              isAdmin={isAdmin} categories={topicCategories}
              categoryId={topics.find((t) => t.id === selectedTopicId)?.category ?? null}
              onCategoryChanged={handleTopicCategoryChanged}
            />
          ) : (
            <TopicDetail
              topic={selectedTopic} loading={topicLoading} focusTurn={focusTurn}
              onWordSelect={(wordId, wordText, wordType) => { handleWordSelect(wordId, wordText, wordType); setWordSheetOpen(true); }}
              onClearWord={handleClearWord} selectedWordId={selectedWordId} isAdmin={isAdmin}
              onTopicUpdate={(updatedTopic) => setSelectedTopic(updatedTopic)}
              categories={topicCategories} onCategoryChanged={handleTopicCategoryChanged}
              onTurnSaved={refreshTopicDetail}
              onStartChat={() => { setSelectedWordId(null); setShowChat(true); }}
              onStartQuiz={() => { setSelectedWordId(null); setShowChat(false); setShowFreeChat(false); setQuizMode("learn"); setShowQuiz(true); }}
                  onStartAiQuiz={() => { setSelectedWordId(null); setShowChat(false); setShowFreeChat(false); setShowQuiz(false); setShowAiQuiz(true); }}
              onWordTypeChange={handleTopicWordTypeChange}
              onTopicDeleted={async () => {
                setSelectedTopicId(null);
                setSelectedTopic(null);
                await reloadTopics();
                loadProgress();
                setActiveTab("topics");
              }}
            />
          )}
        </div>

        <div className={`dark-scope absolute inset-0 overflow-y-auto pb-16 p-4 transition-transform duration-300 ease-in-out ${mobilePanel("search")}`} style={{ background: "var(--ink)" }}>
          {selectedQuranicTopicId && selectedQuranicWord ? (
            <div className="p-2">
              <div className="flex items-center justify-between mb-3">
                <h2 className="text-base font-semibold text-slate-700">Word Detail</h2>
                <div className="flex items-center gap-2">
                  <span className="text-xs font-mono text-slate-400">{selectedQuranicWord.word.location || selectedQuranicWord.verseKey}</span>
                  {selectedQuranicWord.word.page_number && (
                    <span className="text-xs text-slate-400">· p.{selectedQuranicWord.word.page_number}</span>
                  )}
                  <button onClick={() => { setSelectedQuranicWord(null); setActiveTab("detail"); }} className="text-slate-400 hover:text-slate-600 text-lg leading-none">&times;</button>
                </div>
              </div>
              <p className="text-5xl font-arabic text-slate-800 text-center mb-2" dir="rtl">{selectedQuranicWord.word.arabic}</p>
              {selectedQuranicWord.word.transliteration && (
                <p className="text-sm text-slate-500 text-center italic mb-1">{selectedQuranicWord.word.transliteration}</p>
              )}
              {selectedQuranicWord.word.meaning && (
                <p className="text-lg font-medium text-slate-800 text-center mb-4">{selectedQuranicWord.word.meaning}</p>
              )}
              {selectedQuranicWord.word.audio_url && (
                <button
                  onClick={() => { const a = new Audio(selectedQuranicWord.word.audio_url); a.play(); }}
                  className="w-full py-2 rounded-lg dt-ghost-btn text-sm transition-colors flex items-center justify-center gap-2 mb-4"
                >
                  <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24"><path d="M8 5v14l11-7z"/></svg>
                  Play Word
                </button>
              )}
              {selectedQuranicWord.verseTranslation && (
                <div className="mt-2 p-3 bg-[var(--gold-bg)] rounded-xl border border-[var(--panel-line)]">
                  <p className="text-xs font-semibold text-amber-700 mb-1">Verse {selectedQuranicWord.verseKey}</p>
                  <p className="text-sm text-dt-parchment leading-relaxed">{selectedQuranicWord.verseTranslation.replace(/<[^>]+>/g, "")}</p>
                </div>
              )}
              {/* Word metadata from imported curriculum */}
              {selectedQuranicWord.word.metadata && (
                <div className="mt-3 p-3 surface-glass rounded-xl">
                  {/* Header row */}
                  <div className="flex items-baseline justify-between mb-2">
                    <p className="text-xs font-semibold text-slate-600">Morphological Analysis</p>
                    <div className="flex items-center gap-2">
                      {isAdmin && !editingMorphology && (
                        <button
                          onClick={() => {
                            const m = selectedQuranicWord.word.metadata;
                            setMorphologyEditValues({
                              grammar_label_ar: m.grammar_label_ar || "",
                              morphology_summary: m.morphology_summary || "",
                              frequency_total: m.frequency_total ?? "",
                              lemma: m.lemma || "",
                              root: m.root || "",
                            });
                            setEditingMorphology(true);
                            setMorphologySaveResult(null);
                          }}
                          className="text-[10px] text-slate-400 hover:text-slate-600 flex items-center gap-0.5"
                        >✏ Edit</button>
                      )}
                      <a href="http://corpus.quran.com" target="_blank" rel="noopener noreferrer" className="text-[10px] text-slate-400 hover:text-slate-600 hover:underline">Quranic Arabic Corpus</a>
                    </div>
                  </div>

                  {/* Corrections banner — transient feedback after save/revert */}
                  {morphologySaveResult && morphologySaveResult.changed?.length > 0 && (
                    <div className="mb-2 p-2 rounded-lg bg-emerald-50 border border-emerald-200 text-xs">
                      <p className="font-semibold text-emerald-700 mb-1">
                        {morphologySaveResult.changed.some(c => c.reverted) ? "↺ Reverted to original:" : "✓ Saved corrections:"}
                      </p>
                      {morphologySaveResult.changed.map(c => (
                        <div key={c.field} className="text-emerald-800">
                          <span className="font-medium capitalize">{c.field.replace(/_/g, " ")}</span>:{" "}
                          <span className="line-through text-slate-400">{String(c.original ?? "—")}</span>{" → "}
                          <span>{String(c.corrected ?? "—")}</span>
                        </div>
                      ))}
                    </div>
                  )}

                  {editingMorphology ? (
                    /* ── Edit form ── */
                    <div className="space-y-2 text-sm">
                      {[
                        { key: "grammar_label_ar", label: "Type (Arabic)", dir: "rtl" },
                        { key: "morphology_summary", label: "Morphology (raw)", dir: "ltr" },
                        { key: "frequency_total", label: "Frequency in Quran", dir: "ltr", type: "number" },
                        { key: "lemma", label: "Lemma (BW)", dir: "ltr" },
                        { key: "root", label: "Root (BW)", dir: "ltr" },
                      ].map(({ key, label, dir, type }) => (
                        <div key={key}>
                          <label className="block text-xs text-slate-500 mb-0.5">{label}</label>
                          <input
                            type={type || "text"}
                            dir={dir}
                            value={morphologyEditValues[key] ?? ""}
                            onChange={e => setMorphologyEditValues(v => ({ ...v, [key]: e.target.value }))}
                            className="w-full text-sm border border-slate-300 rounded-lg px-2 py-1 focus:outline-none focus:border-[var(--gold)] font-mono"
                          />
                        </div>
                      ))}
                      <div className="flex gap-2 pt-1">
                        <button
                          onClick={handleSaveMorphologyEdit}
                          disabled={morphologySaving}
                          className="flex-1 py-1.5 text-xs rounded-lg bg-dt-gold-bright text-dt-ink hover:brightness-105 disabled:opacity-50"
                        >{morphologySaving ? "Saving…" : "Save"}</button>
                        <button
                          onClick={() => setEditingMorphology(false)}
                          className="flex-1 py-1.5 text-xs rounded-lg border border-slate-300 text-slate-600 hover:bg-slate-100"
                        >Cancel</button>
                      </div>
                    </div>
                  ) : (
                    /* ── Display mode ── */
                    <>
                      <dl className="space-y-1.5 text-sm">
                        {(parsedMorphology?.type_ar || selectedQuranicWord.word.metadata.grammar_label_ar) && (
                          <div className="flex justify-between">
                            <dt className="text-slate-500">Type</dt>
                            <dd className="font-arabic text-slate-800 font-medium">
                              {parsedMorphology?.type_ar || selectedQuranicWord.word.metadata.grammar_label_ar}
                            </dd>
                          </div>
                        )}
                        {parsedMorphology?.form_ar && (
                          <div className="flex justify-between">
                            <dt className="text-slate-500">Form</dt>
                            <dd className="font-arabic text-slate-800">{parsedMorphology.form_ar}</dd>
                          </div>
                        )}
                        {parsedMorphology?.number_ar && (
                          <div className="flex justify-between">
                            <dt className="text-slate-500">Number</dt>
                            <dd className="font-arabic text-slate-800">{parsedMorphology.number_ar}</dd>
                          </div>
                        )}
                        {parsedMorphology?.gender_ar && (
                          <div className="flex justify-between">
                            <dt className="text-slate-500">Gender</dt>
                            <dd className="font-arabic text-slate-800">{parsedMorphology.gender_ar}</dd>
                          </div>
                        )}
                        {(correctedCase || parsedMorphology?.case_ar) && (
                          <div className="flex justify-between items-center">
                            <dt className="text-slate-500">Case</dt>
                            <dd className="flex items-center gap-1.5">
                              <span className="font-arabic text-slate-800 font-medium">
                                {correctedCase ? correctedCase.case_label_ar : parsedMorphology.case_ar}
                              </span>
                              {correctedCase?.case_source === "derived" && (
                                <span className="text-xs text-slate-400">(corrected)</span>
                              )}
                            </dd>
                          </div>
                        )}
                        {parsedMorphology?.root_ar && (
                          <div className="flex justify-between">
                            <dt className="text-slate-500">Root</dt>
                            <dd className="font-arabic text-slate-800">{parsedMorphology.root_ar}</dd>
                          </div>
                        )}
                        {selectedQuranicWord.word.metadata.frequency_total != null && (
                          <div className="flex justify-between">
                            <dt className="text-slate-500">Frequency in Quran</dt>
                            <dd className="text-slate-800 font-arabic">{selectedQuranicWord.word.metadata.frequency_total} مرة</dd>
                          </div>
                        )}
                      </dl>
                      {selectedQuranicWord.word.metadata.morphology_summary && (
                        <div className="mt-2.5 pt-2 border-t border-slate-200">
                          <button
                            onClick={() => setShowAdvancedMorphology(v => !v)}
                            className="text-xs text-slate-400 hover:text-slate-600 transition-colors"
                          >
                            {showAdvancedMorphology ? "Hide Details ▴" : "Advanced ▾"}
                          </button>
                          {showAdvancedMorphology && (
                            <>
                              <dl className="mt-2 space-y-1.5 text-xs text-slate-500">
                                {selectedQuranicWord.word.metadata.lemma && (
                                  <div className="flex justify-between">
                                    <dt>Lemma</dt>
                                    <dd className="font-mono">{selectedQuranicWord.word.metadata.lemma}</dd>
                                  </div>
                                )}
                                {selectedQuranicWord.word.metadata.root && (
                                  <div className="flex justify-between">
                                    <dt>Root (BW)</dt>
                                    <dd className="font-mono">{selectedQuranicWord.word.metadata.root}</dd>
                                  </div>
                                )}
                                <div className="flex flex-col gap-0.5 pt-0.5">
                                  <dt>Morphology</dt>
                                  <dd className="font-mono break-all leading-relaxed">
                                    {selectedQuranicWord.word.metadata.morphology_summary}
                                  </dd>
                                </div>
                              </dl>
                              {/* Overrides indicator — admin only */}
                              {isAdmin && !editingMorphology && selectedQuranicWord.word.metadata.morphology_overrides && Object.keys(selectedQuranicWord.word.metadata.morphology_overrides).length > 0 && (
                                <div className="mt-2 p-2 rounded-lg bg-amber-50 border border-amber-200 text-xs">
                                  <div className="flex items-center justify-between mb-1">
                                    <p className="font-semibold text-amber-700">⚠ Manually corrected:</p>
                                    <button
                                      onClick={() => handleRevertMorphologyFields(Object.keys(selectedQuranicWord.word.metadata.morphology_overrides))}
                                      disabled={morphologySaving}
                                      className="text-[10px] text-amber-600 hover:text-amber-800 underline disabled:opacity-50"
                                    >Revert all</button>
                                  </div>
                                  {Object.entries(selectedQuranicWord.word.metadata.morphology_overrides).map(([field, v]) => (
                                    <div key={field} className="flex items-center justify-between gap-1 text-amber-800">
                                      <span>
                                        <span className="font-medium capitalize">{field.replace(/_/g, " ")}</span>:{" "}
                                        <span className="line-through text-slate-400">{String(v.original ?? "—")}</span>{" → "}
                                        <span>{String(v.current ?? "—")}</span>
                                      </span>
                                      <button
                                        onClick={() => handleRevertMorphologyFields([field])}
                                        disabled={morphologySaving}
                                        className="shrink-0 text-[10px] text-amber-600 hover:text-amber-800 underline disabled:opacity-50"
                                      >↺</button>
                                    </div>
                                  ))}
                                </div>
                              )}
                            </>
                          )}
                        </div>
                      )}
                    </>
                  )}
                </div>
              )}

              {/* Verb conjugation link panel (all users see linked verb; admin can link/unlink) */}
              {selectedQuranicWord.word.metadata && (
                <QuranVerbLinkPanel
                  word={selectedQuranicWord.word}
                  loading={verbLinkLoading}
                  isAdmin={isAdmin}
                  onLink={(senseId) => handleLinkQuranVerb(selectedQuranicWord.word.metadata.id, senseId)}
                  onUnlink={() => handleUnlinkQuranVerb(selectedQuranicWord.word.metadata.id, selectedQuranicWord.word.conjugation_match)}
                />
              )}
            </div>
          ) : (
            searchPanelJSX
          )}
        </div>
      </div>

      {/* Mobile word-detail bottom sheet (mobile only; desktop uses the side panel) */}
      {wordSheetOpen && selectedWordId && (
        <div className="md:hidden fixed inset-0 z-30 flex flex-col justify-end">
          {/* scrim */}
          <div
            className="absolute inset-0 bg-ink-900/30"
            onClick={handleClearWord}
            aria-hidden="true"
          />
          {/* sheet */}
          <div className="relative w-full max-h-[88vh] flex flex-col bg-parchment-50 rounded-t-2xl border-t border-parchment-300 shadow-soft-lg animate-rise">
            {/* fixed header: grab handle + close */}
            <div className="shrink-0 px-4 pt-2.5 pb-2 border-b border-parchment-200">
              <div className="w-9 h-1 rounded-full bg-parchment-300 mx-auto mb-2" />
              <div className="flex items-center justify-between">
                <span className="text-xs uppercase tracking-wide text-ink-400">Word detail</span>
                <button
                  onClick={handleClearWord}
                  className="text-ink-400 hover:text-ink-600 text-xl leading-none"
                  aria-label="Close"
                >
                  &times;
                </button>
              </div>
            </div>
            {/* scrollable body */}
            <div className="flex-1 overflow-y-auto p-4 scroll-elegant">
              <WordDetail
                word={selectedWord} occurrence={selectedWordOccurrence} loading={wordLoading} error={wordError}
                onClear={handleClearWord} topicsForWord={wordTopics}
                topicsForWordLoading={wordTopicsLoading} topicsForWordError={wordTopicsError}
                onTopicSelect={(topicId, topicName) => { handleTopicSelectFromWord(topicId, topicName); setWordSheetOpen(false); setActiveTab("detail"); }}
                onFormWordClick={handleFormWordClick} isAdmin={isAdmin}
                onWordUpdate={(updatedWord) => setSelectedWord(updatedWord)}
                currentTopicId={selectedTopicId} currentWordType={selectedWordType} topicOwnedByUser={!!selectedTopic?.is_owner}
                onTopicWordTypeChange={handleTopicWordTypeChange}
              />
            </div>
          </div>
        </div>
      )}

      {/* Mobile bottom tab bar */}
      <nav className="md:hidden fixed bottom-0 left-0 right-0 z-20 bg-parchment-50/95 backdrop-blur border-t border-parchment-300 flex shadow-soft-lg">
        <button className={tabClass("topics")} onClick={() => setActiveTab("topics")}>
          <svg className="w-5 h-5 mb-0.5" fill="none" stroke="currentColor" strokeWidth={1.8} viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" d="M4 6h16M4 12h16M4 18h16" />
          </svg>
          Learn
        </button>
        <button className={tabClass("search")} onClick={() => setActiveTab("search")}>
          <svg className="w-5 h-5 mb-0.5" fill="none" stroke="currentColor" strokeWidth={1.8} viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
          </svg>
          Search
        </button>
      </nav>

      {showAdminSettings && <AdminSettingsPanel onClose={() => setShowAdminSettings(false)} />}
      {showInstitutePanel && (
        <InstituteAdminPanel
          institute={currentUser?.institute}
          onClose={() => setShowInstitutePanel(false)}
          onInstituteChange={() =>
            fetchCurrentUser().then((me) => setCurrentUser(me)).catch(() => {})
          }
        />
      )}

      {showWordBank && (
        <WordBank onClose={() => setShowWordBank(false)} isAdmin={isAdmin} />
      )}

      {showCreateTopic && (
        <CreateTopicModal
          isAdmin={isAdmin}
          ownedCount={topics.filter((t) => t.is_owner).length}
          onClose={() => setShowCreateTopic(false)}
          onCreated={reloadTopics}
        />
      )}

      {showReading && (
        <Reading onClose={() => setShowReading(false)} onTopicCreated={() => reloadTopics()} />
      )}

      {showTyping && <TypingView onClose={() => setShowTyping(false)} />}

      {addWordModalText !== null && isAdmin && (
        <AddWordModal
          initialText={addWordModalText}
          onClose={() => setAddWordModalText(null)}
          onCreated={handleWordCreated}
        />
      )}

      {showWordBankReview && isAdmin && (
        <WordBankAdminReview onClose={() => setShowWordBankReview(false)} />
      )}

      {/* Speaking Helper — global, also openable from inside a live practice call.
          z-[60] keeps it above the fullscreen practice-call overlay (z-50). */}
      {showSpeakingHelper && (
        <SpeakingHelper onClose={() => setShowSpeakingHelper(false)} />
      )}

      {(showIrabTool || irabToolText != null) && (
        <IrabTool
          onClose={() => { setShowIrabTool(false); setIrabToolText(null); setIrabToolViewOnly(false); }}
          initialText={irabToolText || ""}
          autoAnalyze={irabToolText != null}
          viewOnly={irabToolText != null && irabToolViewOnly}
        />
      )}

      {showSarfTool && (
        <SarfTool onClose={() => setShowSarfTool(false)} />
      )}

      {/* Leaderboard modal */}
      {showLeaderboard && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40 backdrop-blur-sm">
          <div className="surface-glass rounded-2xl shadow-2xl w-full max-w-3xl max-h-[90vh] flex flex-col overflow-hidden">
            <Leaderboard
              currentUser={currentUser}
              onClose={() => setShowLeaderboard(false)}
            />
          </div>
        </div>
      )}

      {/* Practice Calls — single instance so in-call state survives minimize/maximize */}
      {showPractice && (
        <div
          ref={pipRef}
          className={
            practiceMinimized
              ? "fixed z-50 rounded-2xl overflow-hidden shadow-2xl select-none"
              : "fixed inset-0 z-50 overflow-auto"
          }
          style={
            practiceMinimized && pipPos
              ? { left: pipPos.left, top: pipPos.top, width: PIP_W }
              : { background: "var(--ink)" }
          }
          onPointerMove={undefined}
          onPointerUp={undefined}
          onPointerCancel={undefined}
        >
          {/* Draggable header — only shown when minimized */}
          {practiceMinimized && (
            <div
              className="flex items-center justify-between px-3 py-2 bg-slate-900 border-b border-slate-700 cursor-grab active:cursor-grabbing touch-none"
              onPointerDown={pipPointerDown}
            >
              <div className="flex items-center gap-1.5 pointer-events-none">
                <span className="w-1.5 h-1.5 rounded-full bg-rose-500 animate-pulse" />
                <span className="text-white text-xs font-medium">Practice Call</span>
                <span className="text-slate-500 text-xs ml-1">· drag</span>
              </div>
              <div className="flex items-center gap-2" onPointerDown={(e) => e.stopPropagation()}>
                <button
                  onClick={handleMaximize}
                  title="Expand to full screen"
                  className="text-slate-400 hover:text-white text-xs px-2 py-0.5 rounded hover:bg-slate-700 transition-colors"
                >
                  ↗
                </button>
                <button
                  onClick={() => { setShowPractice(false); setPracticeMinimized(false); setPipPos(null); }}
                  className="text-slate-500 hover:text-white text-sm leading-none"
                >
                  ✕
                </button>
              </div>
            </div>
          )}
          <PracticeCallPage
            onClose={() => { setShowPractice(false); setPracticeMinimized(false); setPipPos(null); }}
            minimized={practiceMinimized}
            onMinimize={handleMinimize}
            onMaximize={handleMaximize}
            onOpenSpeakingHelper={() => setShowSpeakingHelper(true)}
            institute={currentUser?.institute || null}
          />
        </div>
      )}
    </div>
      } />
    </Routes>

    {/* App-level modal — outside Routes so both the app sidebar's "Feed
        Moderation" button and the Majlis header's admin button can open it. */}
    {showFeedAdmin && isAdmin && (
      <FeedAdminReview onClose={() => setShowFeedAdmin(false)} />
    )}

    {/* Floating help button + support panel — outside Routes so they show on
        every authenticated page, /majlis included. Public routes mount in
        index.js and never render this shell. */}
    <FloatingHelpButton onClick={() => setShowHelp(true)} />
    {showHelp && (
      <HelpPanel onClose={() => setShowHelp(false)} currentUser={currentUser} />
    )}
    {showSupportAdmin && isAdmin && (
      <SupportAdminPanel onClose={() => setShowSupportAdmin(false)} />
    )}
    </>
  );
}

export default App;
